import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { createReservation, createReservationAsUser, getUpcomingEvents } from '../services/reservationService';
import { getZonesByRestaurant } from '../services/zoneService';
import { getSeatMap } from '../services/seatMapService';
import { getShiftsForDate, getSlotAvailability } from '../services/shiftService';
import { getEventsByRestaurant } from '../services/eventService';
import { getBlockedDates, getCelebrationPackages } from '../services/restaurantService';
import { getEventTicketTypes, reserveEventTickets, calculateOrderTotal, validatePromoCodePublic, validateReservationPromoCodePublic } from '../services/eventTicketService';
import { sendGuestOtp, verifyGuestOtp, resendGuestOtp } from '../services/guestEmailVerificationService';
import { useAuth } from '../context/AuthContext';
import { toLocalDateString } from '../utils/dateUtils';
import EventPopup from './EventPopup';
import EventCard from './EventCard';
import EventTicketSelector from './EventTicketSelector';
import SeatMap, { describeSelection, SelectionSummary } from './SeatMap';
import { celebrationTotalCents, OccasionPicker, SpecialOccasionHeader } from './CelebrationPackagePicker';
import { normalizeNoteHtml } from '../utils/richText';
import { shouldOpenKikisEventPage, redirectToKikisEvent } from '../utils/kikisRedirect';
import { isValidPhone } from '../utils/phone';
import './EventTicketSelector.css';
import './ReservationForm.css';

// Matches backend computeRequiredSocialMediaCount — floor-based so admins use 'one' for a guaranteed minimum with small orders.
function computeRequiredSocialMediaCount(total, coverage) {
  if (!total) return 0;
  switch (coverage) {
    case 'one': return Math.min(1, total);
    case '25':  return Math.floor(total * 0.25);
    case '50':  return Math.floor(total * 0.50);
    case '75':  return Math.floor(total * 0.75);
    case 'all': return total;
    default:    return total;
  }
}

// ── Duplicate-guest detection ────────────────────────────────────────────────
// When a restaurant collects every guest's own details (Experiences with
// "Same as Buyer" turned OFF, or a regular shift that requires all guests'
// data) guests must not be copy-pasted. These normalizers build comparison keys
// that ignore case / formatting so "John@X.com" and " john@x.com " collide.
const normNameKey = (f, l) => `${(f || '').trim().toLowerCase()} ${(l || '').trim().toLowerCase()}`.trim();
const normEmailKey = (e) => (e || '').trim().toLowerCase();
const normPhoneKey = (p) => (p || '').replace(/\D/g, '');
const normLinkKey = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, '').replace(/\/+$/, '');

// Returns the 1-based position of the first entry whose key repeats an earlier
// entry's, or null when every non-empty key is unique. Empty keys are skipped so
// this only fires on genuinely repeated values, never on blanks.
const findDuplicatePosition = (people, selector) => {
  const seen = new Set();
  for (let i = 0; i < people.length; i++) {
    const key = selector(people[i]);
    if (!key) continue;
    if (seen.has(key)) return i + 1;
    seen.add(key);
  }
  return null;
};

// Runs name/email/phone duplicate checks over a list of guests and returns a
// user-facing error string (or null if clean). Used by both booking flows.
const REQUIRE_NO_DUP_PREFIX = 'All guests must have their own details — no duplication allowed.';
const findGuestDuplicateError = (guests, { checkName = true } = {}) => {
  if (checkName) {
    const dup = findDuplicatePosition(guests, (g) => normNameKey(g.firstName, g.lastName));
    if (dup) return `${REQUIRE_NO_DUP_PREFIX} Guest ${dup} has the same name as another guest.`;
  }
  const dupEmail = findDuplicatePosition(guests, (g) => normEmailKey(g.email));
  if (dupEmail) return `${REQUIRE_NO_DUP_PREFIX} Guest ${dupEmail} has the same email as another guest.`;
  const dupPhone = findDuplicatePosition(guests, (g) => normPhoneKey(g.phone));
  if (dupPhone) return `${REQUIRE_NO_DUP_PREFIX} Guest ${dupPhone} has the same phone number as another guest.`;
  return null;
};

// Returns an error string if the same social media link is reused across guests.
const findSocialMediaDuplicateError = (guests) => {
  const dup = findDuplicatePosition(guests, (g) => normLinkKey(g.socialMediaLink));
  if (dup) return `The same social media link can't be used for more than one guest. Please give guest ${dup} a different link.`;
  return null;
};

// Seat booking entrance rule: a group may be at most maxMenPercent% men. Same maths
// as the API and admin (integer compare, so no rounding surprises).
const getGenderRatio = (men, women, maxMenPercent) => {
  const applicable = men != null && women != null && men + women > 0;
  if (!applicable) return { applicable, menPercent: null, met: null };
  return {
    applicable,
    menPercent: Math.round((men * 100) / (men + women)),
    met: men * 100 <= maxMenPercent * (men + women),
  };
};

// Special Occasion pick: the occasion (e.g. Birthday), one of its packages, extra add-ons
const EMPTY_CELEBRATION_SELECTION = { occasionId: null, packageId: null, addonQuantity: 0 };

const ReservationForm = ({ restaurantId, restaurantName, restaurant, isWidget = false, prefillUser = null, hostAuthenticated = false, initialEvent = null, onInitialEventHandled = null, initialEventShift = null, onInitialEventShiftHandled = null, onSuccessChange = null, onEventDateChange = null, onEventHouseRulesChange = null, onBookingChoiceChange = null, singleEvent = false, onRestart = null }) => {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();

  // Navigate to a payment page. When the form is embedded in a host iframe the
  // gateway can't run inside the small widget frame, so break out and load it in
  // the top window; otherwise navigate within the SPA as usual.
  const goToPayment = (path) => {
    let embedded = false;
    try {
      embedded = window.top !== window.self;
    } catch (e) {
      embedded = true; // cross-origin access threw → we are framed
    }
    if (embedded) {
      // Mark the payment page so it can hide site chrome (navbar) — it's now a
      // standalone top-level checkout reached from the embedded widget.
      const sep = path.includes('?') ? '&' : '?';
      const absolute = `${window.location.origin}${path}${sep}embed=1`;
      breakOutTo(absolute);
      return;
    }
    navigate(path);
  };

  // Break the cross-origin iframe out to an absolute top-level URL. A direct
  // top-navigation from inside the iframe is blocked once the create-reservation
  // await consumes the click's user activation, so we ALSO ask the host page to
  // navigate its own top window (it listens for this message); the direct attempts
  // are the fallback for the standalone widget link.
  const breakOutTo = (absoluteUrl) => {
    try {
      window.parent.postMessage(
        { source: 'tali-reservation', type: 'navigate', url: absoluteUrl },
        '*',
      );
    } catch (e) {
      /* noop — fall through to the direct attempts below */
    }
    try {
      window.top.location.href = absoluteUrl;
    } catch (e) {
      window.location.href = absoluteUrl;
    }
  };

  // Send the guest straight to a FULL-PAGE payment URL (not the in-site /payment
  // iframe wrapper). Used when the widget is embedded in a host page (e.g. Kiki's):
  // the /payment wrapper is a fixed-height, overflow:hidden container, so on mobile
  // a tall deposit form inside it can't be scrolled down to the Pay button. Loading
  // the payment page as a standalone top-level page — exactly like the emailed
  // payment link, which scrolls fine — restores native scrolling.
  const goToPaymentAbsolute = (rawUrl) => {
    const absolute = /^https?:\/\//i.test(rawUrl)
      ? rawUrl
      : `${window.location.origin}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
    breakOutTo(absolute);
  };

  const formContainerRef = useRef(null);
  const timeSlotsContainerRef = useRef(null);
  const quickDatesContainerRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedRequiresApproval, setSubmittedRequiresApproval] = useState(false);
  const [submittedToWaitlist, setSubmittedToWaitlist] = useState(false);
  const [submittedPaymentPending, setSubmittedPaymentPending] = useState(false);
  const [waitlistInfo, setWaitlistInfo] = useState({ position: null, reason: null });
  const [error, setError] = useState('');
  // Per-field "missing required" flags, set on validation and cleared as the
  // user fills each field. Drives the red highlight on the offending fields.
  const [fieldErrors, setFieldErrors] = useState({});
  const [events, setEvents] = useState([]);
  const [externalEvents, setExternalEvents] = useState([]);
  const [externalEventsLoaded, setExternalEventsLoaded] = useState(false);
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [selectedEventIndex, setSelectedEventIndex] = useState(0);
  const [zones, setZones] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  // Dates a private experience this guest can't see closes to regular bookings.
  const [privateBlockedDates, setPrivateBlockedDates] = useState([]);
  const [soldOutDates, setSoldOutDates] = useState([]);
  const [closedWeekdays, setClosedWeekdays] = useState([]);
  const [blockedDatesLoaded, setBlockedDatesLoaded] = useState(false);
  const [nextAvailableDate, setNextAvailableDate] = useState(null);
  const [slotAvailability, setSlotAvailability] = useState({}); // Map of shiftId -> availability data
  // Label-first selection: which labeled shift the guest picked (null = not yet picked).
  // Used when a date has shifts with showLabelToCustomers ON (e.g. parallel/overlapping shifts).
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  // Shift to pre-select once its date's shifts have loaded. Set when arriving via
  // a widget deep-link (?shiftId=…); held separately because the date-change
  // effect clears selectedShiftId, so we re-apply it after the fetch.
  const [pendingShiftId, setPendingShiftId] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [dateViewMode, setDateViewMode] = useState('quick'); // 'quick' or 'calendar'
  const [calendarToast, setCalendarToast] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showCustomGuestInput, setShowCustomGuestInput] = useState(false);
  const [customGuestValue, setCustomGuestValue] = useState('');

  // Seat booking (theater mode): the guest picks chairs on a seat map, and the
  // party size follows the number of chairs. Only when the restaurant turns it on.
  const seatMode = restaurant?.seatBookingEnabled === true;
  const [seatMap, setSeatMap] = useState(null);
  const [seatMapLoading, setSeatMapLoading] = useState(false);
  const [seatMapError, setSeatMapError] = useState('');
  const [selectedSeatIds, setSelectedSeatIds] = useState([]);
  const [seatNotice, setSeatNotice] = useState('');
  const selectedSeatIdsRef = useRef([]);
  // The 15s seat-map poll runs from an old render, so it reads the live step here.
  const stepRef = useRef(1);
  // How many of the picked chairs are for men and for women (must add up to the chairs).
  const [menCount, setMenCount] = useState(0);
  const [womenCount, setWomenCount] = useState(0);
  const maxMenPercent = restaurant?.maxMenPercent ?? 60;

  // Event ticketing state
  const [ticketMode, setTicketMode] = useState(false); // True when purchasing event tickets
  const [selectedEvent, setSelectedEvent] = useState(null); // The event being purchased
  const [selectedTickets, setSelectedTickets] = useState([]); // Selected ticket types with quantities
  const [ticketTotal, setTicketTotal] = useState(0); // Total price for tickets
  const [ticketFeeConfig, setTicketFeeConfig] = useState({ fixedFeeCents: 0, feePercent: 0 }); // Fee config
  const [ticketStep, setTicketStep] = useState(1); // 1: Select tickets, 2: Buyer info, 3: Ticket holders
  const [ticketHolders, setTicketHolders] = useState([]); // Per-ticket holder info
  const [ticketSubmitSuccess, setTicketSubmitSuccess] = useState(false); // Ticket submission success
  const [ticketAutoApproved, setTicketAutoApproved] = useState(false); // Auto-approved (no approval needed)

  // Promo code state for event-ticket flow
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null); // { code, discountPercent, perItem: [{ticketTypeId, discountCents}] }
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // Promo code for a REGULAR (non-experience) reservation. Kept separate from the
  // experience promo above so the two flows can't overwrite each other's state.
  const [resPromoInput, setResPromoInput] = useState('');
  const [resPromo, setResPromo] = useState(null);
  const [resPromoError, setResPromoError] = useState('');
  const [resPromoLoading, setResPromoLoading] = useState(false);

  // Booking type selection state (inline, not modal)
  const [bookingTypeChoice, setBookingTypeChoice] = useState(null); // null, 'regular', or 'tickets'
  const [bookingTypeEvents, setBookingTypeEvents] = useState([]); // Non-blocking events for the selected date

  // Step 1 data
  const [reservationDetails, setReservationDetails] = useState({
    date: '',
    time: '',
    guests: '',
    specialOccasion: '',
    eventId: null,
  });

  // Payment currency selection
  const [selectedCurrency, setSelectedCurrency] = useState('EGP');

  // Special occasion checkbox state
  const [hasSpecialOccasion, setHasSpecialOccasion] = useState(false);

  // Special Occasions offered on the booking form (restaurants that set them up,
  // e.g. Studio Samara): the guest picks an occasion (e.g. Birthday), then one of
  // its packages. A restaurant whose occasions are all hidden shows no Special
  // Occasion line; one that never set any up keeps the plain occasion text box.
  const [celebrationOccasions, setCelebrationOccasions] = useState([]);
  const [occasionsInfo, setOccasionsInfo] = useState({ loaded: false, usesOccasions: false });
  const [celebrationSelection, setCelebrationSelection] = useState(EMPTY_CELEBRATION_SELECTION);
  const occasionsMode = celebrationOccasions.length > 0;
  // Wait for the answer so the plain box doesn't flash up and disappear
  const showSpecialOccasion = occasionsInfo.loaded && (occasionsMode || !occasionsInfo.usesOccasions);
  // Experience bookings: every paid booking offers the restaurant's occasions (the
  // package is paid with the tickets, so a free booking has nothing to add it to)
  const ticketOccasionsMode = occasionsMode && selectedTickets.some((t) => t.priceCents > 0);
  const selectedOccasion = hasSpecialOccasion && occasionsMode
    ? celebrationOccasions.find((o) => o.id === celebrationSelection.occasionId) || null
    : null;
  const selectedCelebrationPackage =
    selectedOccasion?.packages.find((p) => p.id === celebrationSelection.packageId) || null;

  const handleSpecialOccasionToggle = (checked) => {
    setHasSpecialOccasion(checked);
    if (!checked) {
      setReservationDetails((prev) => ({ ...prev, specialOccasion: '' }));
      setCelebrationSelection(EMPTY_CELEBRATION_SELECTION);
      setFieldErrors((prev) => ({ ...prev, celebrationOccasion: false, celebrationPackage: false }));
    }
  };

  // Experience bookings: the Special Occasion box sits under the tickets (first
  // screen) and the package joins the order total there and on the last step.
  const ticketPackageCents = ticketOccasionsMode && selectedCelebrationPackage
    ? celebrationTotalCents(selectedCelebrationPackage, celebrationSelection.addonQuantity)
    : 0;
  const ticketPackageLine = ticketPackageCents > 0
    ? {
        label: `${selectedCelebrationPackage.name} ${selectedOccasion.name.toLowerCase()} package${
          selectedCelebrationPackage.addonName && celebrationSelection.addonQuantity > 0
            ? ` + ${celebrationSelection.addonQuantity} × ${selectedCelebrationPackage.addonName}`
            : ''
        }`,
        cents: ticketPackageCents,
        feeCents: Math.round(ticketPackageCents * ticketFeeConfig.feePercent / 100),
      }
    : null;

  // An occasion must be picked, and a package when it has some. Returns false and
  // shows what is missing when the guest can't go on yet.
  const checkTicketOccasion = () => {
    if (!ticketOccasionsMode || !hasSpecialOccasion) return true;
    let message = '';
    if (!selectedOccasion) {
      setFieldErrors((prev) => ({ ...prev, celebrationOccasion: true }));
      message = 'Please choose your occasion, or switch off Special Occasion.';
    } else if (selectedOccasion.packages.length > 0 && !selectedCelebrationPackage) {
      setFieldErrors((prev) => ({ ...prev, celebrationPackage: true }));
      message = `Please choose a ${selectedOccasion.name.toLowerCase()} package.`;
    }
    if (!message) return true;
    setError(message);
    document.getElementById('ticketSpecialOccasion')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  };

  const renderTicketOccasion = () => (ticketOccasionsMode ? (
    <div className="form-section mt-3 mb-3" id="ticketSpecialOccasion">
      <SpecialOccasionHeader
        id="hasTicketSpecialOccasion"
        checked={hasSpecialOccasion}
        onChange={handleSpecialOccasionToggle}
      />
      {hasSpecialOccasion && (
        <div className="mt-3">
          <OccasionPicker
            occasions={celebrationOccasions}
            value={celebrationSelection}
            onChange={(selection) => {
              setCelebrationSelection(selection);
              if (fieldErrors.celebrationOccasion || fieldErrors.celebrationPackage) {
                setFieldErrors((prev) => ({ ...prev, celebrationOccasion: false, celebrationPackage: false }));
              }
            }}
            note={reservationDetails.specialOccasion}
            onNoteChange={(value) => setReservationDetails((prev) => ({ ...prev, specialOccasion: value }))}
            errors={{
              occasion: !!fieldErrors.celebrationOccasion,
              package: !!fieldErrors.celebrationPackage,
            }}
          />
        </div>
      )}
    </div>
  ) : null);

  useEffect(() => {
    if (!restaurantId) {
      setCelebrationOccasions([]);
      setOccasionsInfo({ loaded: true, usesOccasions: false });
      return undefined;
    }
    let cancelled = false;
    getCelebrationPackages(restaurantId)
      .then((data) => {
        if (cancelled) return;
        setCelebrationOccasions(data?.occasions || []);
        setOccasionsInfo({ loaded: true, usesOccasions: !!data?.usesOccasions });
      })
      .catch((err) => {
        console.error('Failed to load special occasions', err);
        if (cancelled) return;
        // Fall back to the plain occasion text box
        setCelebrationOccasions([]);
        setOccasionsInfo({ loaded: true, usesOccasions: false });
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  // Collapsible sections state
  const [allergiesExpanded, setAllergiesExpanded] = useState(false);
  const [dietaryExpanded, setDietaryExpanded] = useState(false);

  // Email OTP verification state (event tickets only)
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');

  // Step 2 data
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    allergies: [],
    dietaryPreference: [],
    comments: '',
    socialMediaLink: '',
  });

  // Per-guest data, used only when the selected shift has "Request All Guests
  // Data" enabled or the restaurant uses seat booking. One entry per guest in the party:
  // { firstName, lastName, email, phone, socialMediaLink }. socialMediaLink is
  // only collected/shown when the shift also has "Ask for Social Media Link" on.
  const [guestDetails, setGuestDetails] = useState([]);

  // Guest 1 is the lead booker, so we prefill it from the head data above and
  // keep it in sync as the booker types — until the booker edits Guest 1
  // directly, after which we stop overwriting their changes.
  const guestZeroTouchedRef = useRef(false);

  // Notify parent when success state changes (used by widget to hide notes).
  // On the standalone experience page (singleEvent) the ticket success screen
  // should also count, so its hero + house-rules hide on success AND come back
  // when the guest taps "Make Another Booking" (which clears ticketSubmitSuccess).
  // Gated on singleEvent so the embedded widget's behaviour is unchanged.
  useEffect(() => {
    if (onSuccessChange) {
      onSuccessChange(success || (singleEvent && ticketSubmitSuccess));
    }
  }, [success, ticketSubmitSuccess, singleEvent, onSuccessChange]);

  // The experience whose notes/house rules should be shown. When a date has
  // multiple experiences we follow the one the guest actually selected; with a
  // single experience there's only one to show. Null until a choice is made
  // among multiple experiences.
  const activeNoteEvent =
    selectedEvent || (bookingTypeEvents.length === 1 ? bookingTypeEvents[0] : null);

  useEffect(() => {
    if (onEventDateChange) {
      // While events are still loading, treat as event date to prevent flash
      onEventDateChange(!externalEventsLoaded || bookingTypeEvents.length > 0);
    }
    if (onEventHouseRulesChange) {
      // Only surface the experience note once the guest has actually chosen
      // "Experience Bookings" — not just because the date happens to have an
      // experience. (Blocking events auto-select 'tickets', so they still show.)
      // Use the selected experience so multi-experience dates show the right note.
      const showEventNote = bookingTypeChoice === 'tickets' && !!activeNoteEvent;
      onEventHouseRulesChange(showEventNote ? (activeNoteEvent.houseRules || '') : '');
    }
    if (onBookingChoiceChange) {
      // Report the chosen booking type on experience dates so the widget can
      // decide which top-of-page note to show. Null on non-experience dates.
      onBookingChoiceChange(bookingTypeEvents.length > 0 ? bookingTypeChoice : null);
    }
  }, [bookingTypeEvents, bookingTypeChoice, activeNoteEvent, externalEventsLoaded, onEventDateChange, onEventHouseRulesChange, onBookingChoiceChange]);

  // Helper to check if a date is blocked (per-date list OR weekly-closed weekday)
  const isDateBlocked = (dateStr) => {
    if (blockedDates.includes(dateStr)) return true;
    // A hidden private experience blocks regular bookings that day. With no other
    // experience to offer the guest there, the date is simply closed.
    if (privateBlockedDates.includes(dateStr) && !externalEvents.some(ev => ev.date === dateStr)) return true;
    if (closedWeekdays.length > 0) {
      // Noon avoids DST midnight coercion (Africa/Cairo spring-forward edge case).
      const weekday = new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long' });
      if (closedWeekdays.includes(weekday)) return true;
    }
    return false;
  };

  // Helper to check if a date is sold out
  const isDateSoldOut = (dateStr) => {
    return soldOutDates.includes(dateStr);
  };

  // Helper to get external events for a specific date
  const getEventsForDate = (dateStr) => {
    return externalEvents.filter(event => event.date === dateStr);
  };

  // Check if a date has an event that blocks regular reservations
  const hasBlockingEvent = (dateStr) => {
    const events = getEventsForDate(dateStr);
    return events.some(event => event.blocksRegularReservations && event.isActive);
  };

  // Get all blocking events for a date
  const getBlockingEvents = (dateStr) => {
    const events = getEventsForDate(dateStr);
    return events.filter(event => event.blocksRegularReservations && event.isActive);
  };

  // Get non-blocking events for a date
  const getNonBlockingEvents = (dateStr) => {
    const events = getEventsForDate(dateStr);
    return events.filter(event => !event.blocksRegularReservations && event.isActive);
  };

  // Handle clicking the EVENT badge on a date pill
  const handleEventBadgeClick = (e, dateStr) => {
    e.stopPropagation();

    if (isDateSoldOut(dateStr)) {
      handleStep1Change('date', dateStr);
      return;
    }

    // First check for blocking events - show ticket selector inline (no regular reservation option)
    const blockingEvents = getBlockingEvents(dateStr);
    if (blockingEvents.length > 0) {
      setReservationDetails(prev => ({ ...prev, date: dateStr }));
      setBookingTypeEvents(blockingEvents);
      setBookingTypeChoice('tickets');
      setSelectedTickets([]);
      setTicketTotal(0);
      // Auto-select only when there's exactly one blocking event
      if (blockingEvents.length === 1) {
        setSelectedEvent(blockingEvents[0]);
      } else {
        setSelectedEvent(null);
      }
      return;
    }

    // For non-blocking events or regular dates, just set the date
    // handleStep1Change will automatically set up booking type selector if needed
    handleStep1Change('date', dateStr);
  };

  // Handle user choosing regular reservation (inline)
  const handleSelectRegularReservation = () => {
    setBookingTypeChoice('regular');
  };

  // Handle user choosing event tickets (inline)
  const handleSelectEventTickets = (event) => {
    setBookingTypeChoice('tickets');
    setSelectedEvent(event);
    // Reset ticket selection state but don't enter full ticketMode
    setSelectedTickets([]);
    setTicketTotal(0);
  };

  // An experience with "Book from Widget" OFF must NOT be booked inside the
  // widget — it has to open its dedicated page. Used by every entry point that
  // can start a booking (the banner AND the Highlighted Experiences list), so a
  // page-only experience never opens the in-widget ticket flow.
  // Kiki's (configured restaurant id) goes to its own website event page; every
  // other restaurant goes to the Tali standalone /experience/:id page. The
  // widget often runs in a cross-origin iframe, so when framed we navigate the
  // TOP window (full-screen, same tab) with an in-frame fallback.
  const openEventDedicatedPage = (event) => {
    if (shouldOpenKikisEventPage(event.restaurantId ?? restaurantId)) {
      redirectToKikisEvent(event.id);
      return;
    }
    const path = `/experience/${event.id}`;
    const inIframe = typeof window !== 'undefined' && window.self !== window.top;
    if (inIframe) {
      try {
        window.top.location.href = `${window.location.origin}${path}`;
        return;
      } catch (e) {
        /* cross-origin top access blocked — fall back to in-frame nav */
      }
    }
    navigate(path);
  };

  // True when this experience is restricted to its dedicated page (not bookable
  // inside the widget). singleEvent (the dedicated page itself) is exempt.
  const isPageOnlyEvent = (event) =>
    !singleEvent && event && event.bookFromWidget === false;

  // Handle ticket selection change from EventTicketSelector
  const handleTicketSelectionChange = (tickets, total, fees) => {
    setSelectedTickets(tickets);
    setTicketTotal(total);
    if (fees) setTicketFeeConfig(fees);
  };

  // Initialize ticket holders array when moving to step 3
  const initializeTicketHolders = () => {
    const holders = [];
    let globalIndex = 0;
    // The "Same as Buyer" copy control is a per-experience setting. Pre-filling the
    // FIRST holder with the buyer's own data is separate: the buyer is booking for
    // themselves, so the first ticket always starts with their info (editable), and
    // every other ticket starts empty — independent of the same-as-buyer setting.
    const allowSameAsBuyer = selectedEvent?.allowSameAsBuyer !== false;
    selectedTickets.forEach(ticket => {
      for (let i = 0; i < ticket.quantity; i++) {
        const isFirstHolder = globalIndex === 0;
        holders.push({
          ticketTypeId: ticket.ticketTypeId,
          ticketTypeName: ticket.ticketTypeName,
          // First holder always carries the buyer's data; the rest start blank.
          firstName: isFirstHolder ? personalInfo.firstName : '',
          lastName: isFirstHolder ? personalInfo.lastName : '',
          email: isFirstHolder ? personalInfo.email : '',
          phone: isFirstHolder ? personalInfo.phone : '',
          socialMediaLink: '',
          // The same-as-buyer FLAG (drives the copy button / disabled state) still
          // only applies when the experience allows it.
          sameAsBuyer: isFirstHolder && allowSameAsBuyer
        });
        globalIndex++;
      }
    });
    setTicketHolders(holders);
  };

  // Handle ticket holder info change
  const handleTicketHolderChange = (index, field, value) => {
    const newHolders = [...ticketHolders];
    newHolders[index] = { ...newHolders[index], [field]: value };

    // If "same as buyer" is checked, copy buyer info
    if (field === 'sameAsBuyer' && value) {
      newHolders[index] = {
        ...newHolders[index],
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        email: personalInfo.email,
        phone: personalInfo.phone,
        sameAsBuyer: true
      };
    }

    setTicketHolders(newHolders);
  };

  // Toggle "same as buyer" for every ticket holder at once. Mirrors the
  // per-ticket button's behaviour: turning it ON copies buyer info into all
  // holders, turning it OFF only flips the flag and leaves the typed values.
  const applyBuyerInfoToAll = () => {
    if (ticketHolders.length === 0) return;
    const allOn = ticketHolders.every(h => h.sameAsBuyer);
    const newHolders = ticketHolders.map(h => allOn
      ? { ...h, sameAsBuyer: false }
      : {
          ...h,
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          email: personalInfo.email,
          phone: personalInfo.phone,
          sameAsBuyer: true,
        }
    );
    setTicketHolders(newHolders);
  };

  // Submit ticket reservation
  const handleTicketSubmit = async () => {
    setLoading(true);
    setError('');

    // Special Occasion (picked on the first screen): still complete?
    if (!checkTicketOccasion()) {
      setLoading(false);
      scrollToTop();
      return;
    }

    // Social media link validation (event-level coverage threshold)
    if (selectedEvent?.requireSocialMedia) {
      const total = ticketHolders.length;
      const requiredSocial = computeRequiredSocialMediaCount(total, selectedEvent.socialMediaCoverage);
      let filledSocial = 0;
      for (const h of ticketHolders) {
        const link = (h.socialMediaLink || '').trim();
        if (!link) continue;
        try { new URL(link); } catch {
          setError('Please enter a valid social media URL (including https://).');
          setLoading(false);
          return;
        }
        filledSocial++;
      }
      if (filledSocial < requiredSocial) {
        setError(`At least ${requiredSocial} of ${total} booking holders must provide a social media link.`);
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        tickets: selectedTickets.flatMap(ticket => {
          const holders = ticketHolders.filter(h => h.ticketTypeId === ticket.ticketTypeId);
          return holders.map(holder => ({
            ticketTypeId: ticket.ticketTypeId,
            firstName: holder.firstName,
            lastName: holder.lastName,
            email: holder.email,
            phone: holder.phone,
            socialMediaLink: holder.socialMediaLink || undefined
          }));
        }),
        buyerFirstName: personalInfo.firstName,
        buyerLastName: personalInfo.lastName,
        buyerEmail: personalInfo.email,
        buyerPhone: personalInfo.phone,
        promoCode: promo?.code || undefined,
        // Special Occasion: the occasion's name plus the guest's note, and the
        // package (ids only, the API prices it)
        ...(ticketOccasionsMode && selectedOccasion && {
          specialOccasion: [selectedOccasion.name, reservationDetails.specialOccasion?.trim()]
            .filter(Boolean)
            .join(': ')
            .slice(0, 200),
        }),
        ...(ticketOccasionsMode && selectedCelebrationPackage && {
          celebrationPackageId: selectedCelebrationPackage.id,
          celebrationAddonQuantity: selectedCelebrationPackage.addonName ? celebrationSelection.addonQuantity || 0 : 0,
        }),
      };

      const response = await reserveEventTickets(selectedEvent.id, payload);

      // Auto-approved with payment required — redirect to payment page
      if (response.autoApproved && response.paymentUrl) {
        const token = response.order?.paymentToken;
        if (token) {
          goToPayment(`/event-payment/${token}`);
          return;
        }
      }

      setTicketSubmitSuccess(true);
      setTicketAutoApproved(response.autoApproved || false);
      setLoading(false);
      scrollToTop();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit booking. Please try again.');
      setLoading(false);
      scrollToTop();
    }
  };

  // Exit ticket mode and reset
  const exitTicketMode = () => {
    setTicketMode(false);
    setSelectedEvent(null);
    setSelectedTickets([]);
    setTicketTotal(0);
    setTicketStep(1);
    setTicketHolders([]);
    setTicketSubmitSuccess(false);
    setTicketAutoApproved(false);
  };

  // Standalone experience page (singleEvent): after a successful booking, "Make
  // another booking" should restart the SAME experience's ticket flow — NOT drop
  // to the generic reservation form (there is no generic flow on that page). Keeps
  // selectedEvent + ticketMode so the guest stays on the experience.
  const restartTicketBooking = () => {
    // On the standalone experience page, let the parent remount this form fresh
    // (same as a page refresh) so the hero + house-rules/widget notes reliably
    // reappear — a partial state reset alone left some of them hidden.
    if (onRestart) {
      onRestart();
      return;
    }
    setSelectedTickets([]);
    setTicketTotal(0);
    setTicketStep(1);
    setTicketHolders([]);
    setTicketSubmitSuccess(false);
    setTicketAutoApproved(false);
    // Also clear the generic success/submitted flags so the parent (the standalone
    // ExperiencePage) un-hides the hero + house-rules/notes via onSuccessChange —
    // otherwise they only reappear after a manual page refresh.
    setSuccess(false);
    setSubmittedRequiresApproval(false);
    setSubmittedToWaitlist(false);
    setSubmittedPaymentPending(false);
    setError('');
    setTicketMode(true);
  };

  // Quick date options
  const getQuickDates = () => {
    const dates = [];
    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const dateValue = toLocalDateString(date);
      dates.push({
        weekday,
        monthDay,
        value: dateValue,
        isBlocked: isDateBlocked(dateValue),
        isSoldOut: isDateSoldOut(dateValue),
        hasEvent: getEventsForDate(dateValue).length > 0,
      });
    }
    return dates;
  };

  // Check if all quick dates are blocked
  const allQuickDatesBlocked = () => {
    const quickDates = getQuickDates();
    return quickDates.every(d => d.isBlocked);
  };

  // Apply a date chosen from the calendar grid (mirrors the legacy date-input
  // onChange logic: validates blocked/sold-out, routes event dates to tickets).
  const selectCalendarDate = (selectedDate) => {
    const dateObj = new Date(selectedDate + 'T00:00:00');
    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
    const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    // Guests see a blocked / closed date exactly like a sold-out one
    if (isDateBlocked(selectedDate)) {
      setCalendarToast({ type: 'soldout', message: 'This date is sold out.', dayName, formattedDate });
      return;
    }
    if (isDateSoldOut(selectedDate)) {
      setCalendarToast({ type: 'soldout', message: 'This date is sold out.', dayName, formattedDate });
      return;
    }
    setCalendarToast(null);
    setError('');
    if (getEventsForDate(selectedDate).length > 0) {
      setDateViewMode('quick');
      handleEventBadgeClick({ stopPropagation: () => {} }, selectedDate);
      return;
    }
    handleStep1Change('date', selectedDate);
    setCalendarOpen(false); // collapse the grid once a date is chosen
  };

  // Build the cells for the month grid: leading blanks to align the 1st on its
  // weekday, then one Date per day of the month.
  const buildCalendarDays = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const startWeekday = new Date(year, month, 1).getDay(); // 0 = Sunday
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  };

  // Generate time slots from start to end time with given interval
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const generateTimeSlots = (startTime, endTime, interval = 30) => {
    if (!startTime || !endTime) return [];

    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;

    // Handle overnight shifts (e.g., 21:00 to 02:00)
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours
    }

    let currentMinutes = startMinutes;

    while (currentMinutes < endMinutes) {
      // Normalize to handle times past midnight
      const normalizedMinutes = currentMinutes % (24 * 60);
      const hour = Math.floor(normalizedMinutes / 60);
      const min = normalizedMinutes % 60;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      slots.push(timeStr);
      currentMinutes += interval;
    }

    return slots;
  };

  // Helper to convert HH:mm to minutes
  const timeToMinutes = (time) => {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
  };

  // Simple sort: daytime slots first (6am-11:59pm), then late-night slots (12am-5:59am) at the end
  const sortTimeSlotsWithLateNightLast = (slots) => {
    if (!slots || slots.length === 0) return slots;

    const SIX_AM = 6 * 60; // 360 minutes

    return [...slots].sort((a, b) => {
      const [aH, aM] = a.split(':').map(Number);
      const [bH, bM] = b.split(':').map(Number);
      let aMinutes = aH * 60 + aM;
      let bMinutes = bH * 60 + bM;

      // Late-night slots (before 6am) get pushed to the end by adding 24 hours
      if (aMinutes < SIX_AM) aMinutes += 24 * 60;
      if (bMinutes < SIX_AM) bMinutes += 24 * 60;

      return aMinutes - bMinutes;
    });
  };

  // Check if time slot is after midnight (for auto-date-change feature)
  const isNextDaySlot = (timeSlot) => {
    const SIX_AM = 6 * 60;
    const [h, m] = timeSlot.split(':').map(Number);
    const minutes = h * 60 + m;
    return minutes < SIX_AM; // Slots before 6am are considered "next day"
  };

  // Check if a time falls within any shift's time range
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const isTimeInShift = (time) => {
    if (!shifts || shifts.length === 0) return false;
    const timeMinutes = timeToMinutes(time);
    return shifts.some(shift => {
      const startMinutes = timeToMinutes(shift.shiftStartTime);
      let endMinutes = timeToMinutes(shift.shiftEndTime);

      // Handle overnight shifts
      if (endMinutes <= startMinutes) {
        // Shift spans midnight - time is in shift if:
        // - time is after start (e.g., 21:00-23:59), OR
        // - time is before end (e.g., 00:00-02:00)
        return timeMinutes >= startMinutes || timeMinutes < endMinutes;
      }
      return timeMinutes >= startMinutes && timeMinutes < endMinutes;
    });
  };

  // --- Labeled-shift selection (supports parallel / overlapping shifts) ---
  // When a date has shifts flagged "show label to customers", the guest must pick a
  // shift label first; only that shift's time slots are then shown and the whole
  // booking is saved under that shift. This lets two shifts run at the same time
  // (e.g. "Breakfast" vs "Breakfast + Beach Access") without their slots mixing.
  // Event-type (Experience) shifts are included so a venue's experience shift —
  // created with its own label/image — is selectable and bookable in the widget,
  // not hidden. Both regular and event shifts participate in label-first picking.
  const visibleShifts = (shifts || []).filter((s) => s.showLabelToCustomers);
  // Sold out is per shift AND per date: the shift is only unavailable on the
  // dates listed in soldOutDates, and books normally on every other date.
  const isShiftSoldOutOnDate = (shift) =>
    !!shift && (shift.soldOutDates || []).includes(reservationDetails.date);

  // A shift sold out on the chosen date is never auto-selected and never
  // resolves as active, so its time slots stay hidden even when it is the only
  // labeled option.
  const activeLabeledShift =
    visibleShifts.length === 0
      ? null
      : selectedShiftId != null
        ? visibleShifts.find((s) => s.id === selectedShiftId && !isShiftSoldOutOnDate(s)) || null
        : visibleShifts.length === 1 && !isShiftSoldOutOnDate(visibleShifts[0])
          ? visibleShifts[0] // only one labeled option → auto-select it
          : null;
  // True when there are labeled shifts but the guest hasn't chosen one yet.
  const mustChooseShift = visibleShifts.length > 0 && !activeLabeledShift;

  // Get combined time slots: restaurant hours + shift slots (with correct intervals)
  const getCombinedTimeSlots = () => {
    const slotInterval = restaurant?.slotInterval || 30;
    const openingTime = restaurant?.openingTime || '09:00';
    const closingTime = restaurant?.closingTime || '22:00';

    const allSlotsSet = new Set();

    // Shifts sold out on the chosen date contribute no bookable times. This
    // matters for shifts with no customer-facing label, which never render a pill
    // the guest could see is sold out — without this their slots would show and
    // only fail on submit.
    const soldOutShiftIds = new Set(
      (shifts || [])
        .filter(s => (s.soldOutDates || []).includes(reservationDetails.date))
        .map(s => s.id)
    );

    // If we have slotAvailability data, use shift slots from backend (correct intervals)
    if (Object.keys(slotAvailability).length > 0) {
      Object.entries(slotAvailability).forEach(([shiftId, availability]) => {
        if (soldOutShiftIds.has(Number(shiftId))) return;
        if (availability.slots) {
          availability.slots.forEach(slot => {
            const time = slot.time.substring(0, 5);
            allSlotsSet.add(time);
          });
        }
      });
    } else if (shifts && shifts.length > 0) {
      // Fallback: generate shift slots client-side
      shifts.forEach(shift => {
        if (shift.isSoldOut || soldOutShiftIds.has(shift.id)) return;
        const shiftInterval = shift.slotInterval || slotInterval;
        const shiftSlots = generateTimeSlots(shift.shiftStartTime, shift.shiftEndTime, shiftInterval);
        shiftSlots.forEach(slot => allSlotsSet.add(slot));
      });
    }

    // Add restaurant base slots only when there are no shifts
    if (!shifts || shifts.length === 0) {
      const baseSlots = generateTimeSlots(openingTime, closingTime, slotInterval);
      baseSlots.forEach(slot => allSlotsSet.add(slot));
    }

    // Convert to array and sort: daytime first, late-night (after midnight) at the end
    const combinedSlots = sortTimeSlotsWithLateNightLast(Array.from(allSlotsSet));

    return combinedSlots;
  };

  // Get visible shifts (label always shown now; event-type/Experience shifts are
  // included so they appear and can be booked in the widget).
  const getVisibleShifts = () => {
    if (!shifts || shifts.length === 0) return [];
    return shifts.filter(shift => shift.showLabelToCustomers);
  };

  // Get shift for a given time slot (check ALL shifts, not just visible ones)
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const getShiftForTime = (timeSlot) => {
    if (!shifts || shifts.length === 0) return null;

    // Label-first mode: every shown slot belongs to the chosen shift, so the booking
    // always resolves to it (prevents overlapping shifts from being mixed up).
    if (activeLabeledShift) return activeLabeledShift;

    const [slotHour, slotMin] = timeSlot.split(':').map(Number);
    const slotMinutes = slotHour * 60 + slotMin;

    for (const shift of shifts) {
      const [startHour, startMin] = shift.shiftStartTime.split(':').map(Number);
      const [endHour, endMin] = shift.shiftEndTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      // Handle overnight shifts
      if (endMinutes <= startMinutes) {
        // Shift spans midnight - time is in shift if after start OR before end
        if (slotMinutes >= startMinutes || slotMinutes < endMinutes) {
          return shift;
        }
      } else if (slotMinutes >= startMinutes && slotMinutes < endMinutes) {
        return shift;
      }
    }
    return null;
  };

  // The shift that matches the currently selected time, or null when no shift covers it.
  // Drives the conditional rendering of the Social Media Link field in Step 2.
  const selectedShift = useMemo(
    () => (reservationDetails.time ? getShiftForTime(reservationDetails.time) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reservationDetails.time, shifts, selectedShiftId]
  );

  // Seat booking: a chair is taken for the whole shift night, so the map follows the
  // shift (known once a labeled shift is picked, otherwise once a time is picked).
  const seatShift = activeLabeledShift || selectedShift;
  const seatShiftId = seatShift?.id ?? null;
  const maxSeatsPerBooking = seatShift?.maxAllowedGuestsNumber || null;

  // Every guest's name and email: when the shift asks for it, and always for seat
  // booking (each guest gets their own QR code by email).
  const collectAllGuests = seatMode || !!selectedShift?.requestAllGuestsData;

  useEffect(() => {
    selectedSeatIdsRef.current = selectedSeatIds;
  }, [selectedSeatIds]);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  // `announce`: tell the guest when a picked chair is gone. Off when the caller
  // already showed a message of its own (the 409 on submit).
  const loadSeatMap = async ({ quiet = false, announce = true } = {}) => {
    if (!seatMode || !reservationDetails.date || !seatShiftId) return;
    if (!quiet) setSeatMapLoading(true);
    try {
      const data = await getSeatMap(restaurantId, reservationDetails.date, seatShiftId);
      setSeatMap(data);
      setSeatMapError('');
      // Drop any picked chair another guest took since it was picked.
      const available = new Set();
      const labelById = new Map();
      (data.tables || []).forEach((t) => t.seats.forEach((s) => {
        labelById.set(s.id, s.label);
        if (s.state === 'available') available.add(s.id);
      }));
      const picked = selectedSeatIdsRef.current;
      const lost = new Set(picked.filter((id) => !available.has(id)));
      // Tables are booked whole: a table that lost a chair leaves the selection, and
      // the message names the table rather than its chairs.
      const lostTables = (data.tables || []).filter((t) => t.shape !== 'row' && t.seats.some((s) => lost.has(s.id)));
      lostTables.forEach((t) => t.seats.forEach((s) => { if (picked.includes(s.id)) lost.add(s.id); }));
      const kept = picked.filter((id) => !lost.has(id));
      if (kept.length !== picked.length) {
        const lostLabels = [
          ...lostTables.map((t) => `table ${t.tableNumber}`),
          ...picked
            .filter((id) => lost.has(id) && !available.has(id) && !lostTables.some((t) => t.seats.some((s) => s.id === id)))
            .map((id) => `seat ${labelById.get(id) || `#${id}`}`),
        ];
        setSelectedSeatIds(kept);
        // Never let the booking shrink behind the guest's back: the men/women
        // split has to be stated again for the chairs that are left, and Next
        // stays blocked until it adds up (validateStep1).
        setMenCount(0);
        setWomenCount(0);
        const listed = lostLabels.join(', ');
        const message =
          `${listed.charAt(0).toUpperCase()}${listed.slice(1)} ` +
          `${lostLabels.length === 1 ? 'was' : 'were'} just booked by another guest, so ` +
          `${lostLabels.length === 1 ? 'it was' : 'they were'} removed from your selection. ` +
          `Please check your tables and tell us again who's coming.`;
        if (announce) {
          setSeatNotice(message);
          // On step 2 the notice above the seat map is out of sight, and the guest
          // details they are typing are about to change — take them back to the map.
          if (stepRef.current !== 1) {
            setStep(1);
            setError(message);
            scrollToTop();
          }
        }
      }
    } catch (err) {
      setSeatMapError(err.response?.data?.message || 'Could not load the seat map. Please try again.');
    } finally {
      if (!quiet) setSeatMapLoading(false);
    }
  };

  // A new date or shift is a different night: start the seat selection over, and
  // keep the map fresh while the guest chooses so seats others take change colour.
  useEffect(() => {
    setSelectedSeatIds([]);
    setSeatNotice('');
    setMenCount(0);
    setWomenCount(0);
    setSeatMap(null);
    setSeatMapError('');
    if (!seatMode || !reservationDetails.date || !seatShiftId) return undefined;
    loadSeatMap();
    const timer = setInterval(() => loadSeatMap({ quiet: true }), 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatMode, restaurantId, reservationDetails.date, seatShiftId]);

  // Party size is the people coming (men + women). They may be fewer than the chairs
  // of the tables picked: a table is paid for whole.
  useEffect(() => {
    if (!seatMode) return;
    handleStep1Change('guests', menCount + womenCount || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatMode, menCount, womenCount]);

  const handleToggleSeat = (seatId) => {
    setSeatNotice('');
    setSelectedSeatIds((prev) => {
      if (prev.includes(seatId)) return prev.filter((id) => id !== seatId);
      if (maxSeatsPerBooking && prev.length >= maxSeatsPerBooking) {
        setSeatNotice(`You can book up to ${maxSeatsPerBooking} seats at a time.`);
        return prev;
      }
      return [...prev, seatId];
    });
  };

  // Tables are booked whole: tapping one adds or removes all its chairs. Built on the
  // latest selection, so quick taps on several tables all count.
  const handleToggleTable = (table) => {
    setSeatNotice('');
    const ids = table.seats.map((s) => s.id);
    setSelectedSeatIds((prev) => {
      if (ids.some((id) => prev.includes(id))) return prev.filter((id) => !ids.includes(id));
      if (!table.seats.every((s) => s.state === 'available')) return prev;
      if (maxSeatsPerBooking && prev.length + ids.length > maxSeatsPerBooking) {
        setSeatNotice(`You can book up to ${maxSeatsPerBooking} seats at a time, and table ${table.tableNumber} has ${ids.length}.`);
        return prev;
      }
      return [...prev, ...ids];
    });
  };

  // When chairs are removed (by the guest or because another guest took them),
  // trim the men/women counts so they never add up to more than the chairs.
  useEffect(() => {
    const seats = selectedSeatIds.length;
    if (menCount + womenCount <= seats) return;
    const men = Math.min(menCount, seats);
    setMenCount(men);
    setWomenCount(Math.min(womenCount, seats - men));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeatIds.length]);

  const changeGenderCount = (field, delta) => {
    const men = field === 'men' ? menCount + delta : menCount;
    const women = field === 'women' ? womenCount + delta : womenCount;
    const seats = selectedSeatIds.length;
    if (men < 0 || women < 0 || men + women > seats) return;
    setMenCount(men);
    setWomenCount(women);
    if (men + women >= 1) setFieldErrors((prev) => ({ ...prev, genderCounts: false }));
  };

  // The payment group a shift charges for this head count: the guest-count rule
  // with the highest matching minGuests, otherwise the shift's default group.
  // Same resolution as the API, so what we show is what the guest is charged.
  const resolvePaymentGroup = (shift, guests) => {
    const rules = shift?.paymentGroupRulesResolved;
    const count = parseInt(guests, 10);
    if (Array.isArray(rules) && rules.length > 0 && Number.isFinite(count)) {
      const sortedDesc = [...rules].sort((a, b) => b.minGuests - a.minGuests);
      const match = sortedDesc.find((r) => r.minGuests <= count && r.paymentGroup);
      if (match) return match.paymentGroup;
    }
    return shift?.defaultPaymentGroup;
  };

  // Seat booking: the show's usual price per ticket (VAT included), for tables whose
  // category has no price of its own. The API charges each chair the same way.
  const seatPaymentGroup = seatMode
    ? resolvePaymentGroup(seatShift, menCount + womenCount || selectedSeatIds.length || 1)
    : null;
  const seatFallbackTicketCents = seatPaymentGroup && seatPaymentGroup.paymentType === 'per_guest'
    ? Math.round(seatPaymentGroup.priceCents * (seatPaymentGroup.priceExcludesVat ? 1.14 : 1))
    : null;

  // Get shift label for a given time slot (if any visible shift covers that time)
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const getShiftLabelForTime = (timeSlot) => {
    const visibleShifts = getVisibleShifts();
    if (visibleShifts.length === 0) return null;

    const [slotHour, slotMin] = timeSlot.split(':').map(Number);
    const slotMinutes = slotHour * 60 + slotMin;

    for (const shift of visibleShifts) {
      const [startHour, startMin] = shift.shiftStartTime.split(':').map(Number);
      const [endHour, endMin] = shift.shiftEndTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      // Handle overnight shifts
      if (endMinutes <= startMinutes) {
        // Shift spans midnight - time is in shift if after start OR before end
        if (slotMinutes >= startMinutes || slotMinutes < endMinutes) {
          return shift.label;
        }
      } else if (slotMinutes >= startMinutes && slotMinutes < endMinutes) {
        return shift.label;
      }
    }
    return null;
  };

  // Get slot availability info for a given time slot
  const getSlotInfo = (timeSlot, guestCount = null) => {
    const shift = getShiftForTime(timeSlot);
    if (!shift) return { isBlocked: false, remainingCapacity: null, isManuallyBlocked: false, isOverCapacity: false, waitlistCount: 0, allowOverbooking: false, requiresApproval: false, maxAllowedGuestsNumber: null, shiftRemainingCovers: null };

    // Check if slot requires approval
    const slotTime = timeSlot.substring(0, 5); // Normalize to HH:mm
    const requiresApproval = shift.slotsRequiringApproval?.includes(slotTime) || false;

    const availability = slotAvailability[shift.id];
    if (!availability || !availability.slots) return { isBlocked: false, remainingCapacity: null, isManuallyBlocked: false, isOverCapacity: false, waitlistCount: 0, allowOverbooking: false, requiresApproval, maxAllowedGuestsNumber: availability?.maxAllowedGuestsNumber || null, shiftRemainingCovers: availability?.shiftRemainingCovers || null };

    // Find the slot info for this time
    const slotInfo = availability.slots.find(s => s.time === timeSlot || s.time === `${timeSlot}:00`);
    if (!slotInfo) return { isBlocked: false, remainingCapacity: null, isManuallyBlocked: false, isOverCapacity: false, waitlistCount: 0, allowOverbooking: false, requiresApproval, maxAllowedGuestsNumber: availability.maxAllowedGuestsNumber || null, shiftRemainingCovers: availability.shiftRemainingCovers || null };

    // Check if manually blocked by admin - only manually blocked slots are truly blocked
    const isManuallyBlocked = slotInfo.isManuallyBlocked || false;
    const isBlocked = slotInfo.isBlocked || false;

    // Check if this is an over-capacity slot (booking goes to waitlist)
    const isOverCapacity = slotInfo.isOverCapacity || false;
    const waitlistCount = slotInfo.waitlistCount || 0;

    return {
      isBlocked,
      isManuallyBlocked,
      remainingCapacity: slotInfo.remainingCapacity,
      bookedGuests: slotInfo.bookedGuests,
      maxCapacity: slotInfo.maxCapacity,
      allowOverbooking: availability.allowOverbooking,
      isOverCapacity,
      waitlistCount,
      requiresApproval,
      maxAllowedGuestsNumber: availability.maxAllowedGuestsNumber || null,
      shiftRemainingCovers: availability.shiftRemainingCovers || null,
      shiftTotalCovers: availability.shiftTotalCovers || 0,
      shiftMaxCapacity: availability.shiftMaxCapacity || null,
    };
  };

  // Get max allowed guests from shifts (for guest selector limit)
  const getMaxGuestsFromShifts = () => {
    if (Object.keys(slotAvailability).length === 0) return 10; // Default before data loads

    let maxGuests = null;
    Object.values(slotAvailability).forEach(availability => {
      if (availability.maxAllowedGuestsNumber) {
        if (maxGuests === null || availability.maxAllowedGuestsNumber < maxGuests) {
          maxGuests = availability.maxAllowedGuestsNumber;
        }
      }
    });

    return maxGuests || 10; // Default to 10 if no max is set
  };

  const maxGuestsForPills = getMaxGuestsFromShifts();

  // Filter out past times if selected date is today
  const filterPastTimes = (slots) => {
    if (!reservationDetails.date) return slots;

    const todayStr = toLocalDateString(new Date());

    // If selected date is not today, show all slots
    if (reservationDetails.date !== todayStr) {
      return slots;
    }

    // If selected date is today, filter out past times
    const now = new Date();
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();

    return slots.filter(timeSlot => {
      const [slotHour, slotMin] = timeSlot.split(':').map(Number);

      // Late-night slots (before 6 AM) are for the next day, so always show them
      if (isNextDaySlot(timeSlot)) return true;

      // Compare time for regular slots
      if (slotHour > currentHour) return true;
      if (slotHour === currentHour && slotMin > currentMin) return true;

      return false;
    });
  };

  // Slots belonging to a single shift — prefer backend availability, else generate.
  const getSlotsForShift = (shift) => {
    const av = slotAvailability[shift.id];
    if (av && Array.isArray(av.slots) && av.slots.length > 0) {
      return av.slots.map(s => s.time.substring(0, 5));
    }
    const interval = shift.slotInterval || restaurant?.slotInterval || 30;
    return generateTimeSlots(shift.shiftStartTime, shift.shiftEndTime, interval);
  };

  // A regular-reservation promo is validated against the date and priced against
  // the party size, so any change to either invalidates the applied code.
  useEffect(() => {
    setResPromo(null);
    setResPromoError('');
    setResPromoInput('');
  }, [reservationDetails.date, reservationDetails.guests, selectedShiftId]);

  // Label-first mode: show nothing until a shift is chosen, then only that shift's slots.
  const allTimeSlots = mustChooseShift
    ? []
    : activeLabeledShift
      ? sortTimeSlotsWithLateNightLast(getSlotsForShift(activeLabeledShift))
      : getCombinedTimeSlots();
  const filteredPastTimeSlots = filterPastTimes(allTimeSlots);
  // Filter out slots that are manually blocked by admin (hidden from customer view)
  const timeSlots = filteredPastTimeSlots.filter(time => !getSlotInfo(time).isManuallyBlocked);

  // Format time to 12-hour format
  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Scroll to top when form mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // Fetch upcoming events (internal)
    const fetchEvents = async () => {
      try {
        const eventsData = await getUpcomingEvents(restaurantId, 5);
        setEvents(eventsData);
      } catch (err) {
        console.error('Error fetching events:', err);
      }
    };
    fetchEvents();

    // Fetch external events from events platform
    const fetchExternalEvents = async () => {
      try {
        const eventsData = await getEventsByRestaurant(restaurantId);
        const eventsList = eventsData.events || [];
        // A private experience the guest can't see still blocks regular bookings
        // on its date: any experience the guest CAN see that day is offered on its
        // own (as if it blocked reservations), otherwise isDateBlocked closes the day.
        const privateBlocked = eventsData.privateBlockedDates || [];
        setPrivateBlockedDates(privateBlocked);
        setExternalEvents(eventsList.map(ev => (
          privateBlocked.includes(ev.date) ? { ...ev, blocksRegularReservations: true } : ev
        )));
        setExternalEventsLoaded(true);
      } catch (err) {
        console.error('Error fetching external events:', err);
        setExternalEventsLoaded(true);
      }
    };
    fetchExternalEvents();

    // Fetch zones for the restaurant
    const fetchZones = async () => {
      try {
        const zonesData = await getZonesByRestaurant(restaurantId);
        setZones(zonesData || []);
      } catch (err) {
        console.error('Error fetching zones:', err);
        setZones([]);
      }
    };
    fetchZones();

    // Fetch blocked dates and sold out dates for the restaurant
    const fetchBlockedDates = async () => {
      try {
        const data = await getBlockedDates(restaurantId);
        setBlockedDates(data.blockedDates || []);
        setSoldOutDates(data.soldOutDates || []);
        setClosedWeekdays(data.closedWeekdays || []);
        setNextAvailableDate(data.nextAvailableDate);
        setBlockedDatesLoaded(true);
      } catch (err) {
        console.error('Error fetching blocked dates:', err);
        setBlockedDates([]);
        setSoldOutDates([]);
        setClosedWeekdays([]);
        setBlockedDatesLoaded(true);
      }
    };
    fetchBlockedDates();
  }, [restaurantId]);

  // Handle initialEvent passed from parent (e.g. RestaurantDetail)
  useEffect(() => {
    if (!initialEvent) return;
    const eventDate = initialEvent.eventDate
      ? toLocalDateString(new Date(initialEvent.eventDate))
      : '';
    if (eventDate) {
      setReservationDetails(prev => ({ ...prev, date: eventDate }));
      setBookingTypeChoice('tickets');
      setBookingTypeEvents([initialEvent]);
      setSelectedEvent(initialEvent);
      setSelectedTickets([]);
      setTicketTotal(0);
      setDateViewMode('quick');
      setTimeout(() => {
        if (quickDatesContainerRef.current) {
          const pills = quickDatesContainerRef.current.querySelectorAll('.quick-date-pill');
          for (const pill of pills) {
            if (pill.classList.contains('active')) {
              pill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
              break;
            }
          }
        }
      }, 150);
    }
    if (onInitialEventHandled) onInitialEventHandled();
  }, [initialEvent]);

  // Handle initialEventShift passed from parent (widget link with shiftId).
  // Sets the date to the soonest day the shift runs and, for regular labeled
  // shifts, queues the shift for pre-selection (applied once that date's shifts
  // load — see the effect below).
  useEffect(() => {
    if (!initialEventShift) return;

    const isEventShift = initialEventShift.shiftType === 'event';
    const toStr = (d) =>
      !d ? '' : (typeof d === 'string' ? d.split('T')[0] : toLocalDateString(new Date(d)));

    // Soonest upcoming date the shift is bookable: start from today (or the
    // shift's startDate if it's still in the future), then walk forward to the
    // first weekday the shift runs, staying within [startDate, endDate]. Event
    // shifts keep their own single start date.
    const computeShiftDate = () => {
      const startStr = toStr(initialEventShift.startDate);
      if (isEventShift) return startStr;
      const endStr = toStr(initialEventShift.endDate);
      const dowNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const days = Array.isArray(initialEventShift.daysOfWeek)
        ? initialEventShift.daysOfWeek.map((d) => String(d).toLowerCase())
        : [];
      const cursor = new Date();
      cursor.setHours(0, 0, 0, 0);
      if (startStr) {
        const start = new Date(`${startStr}T00:00:00`);
        if (start > cursor) cursor.setTime(start.getTime());
      }
      const end = endStr ? new Date(`${endStr}T00:00:00`) : null;
      for (let i = 0; i < 366; i++) {
        if (end && cursor > end) return '';
        if (days.length === 0 || days.includes(dowNames[cursor.getDay()])) {
          return toLocalDateString(cursor);
        }
        cursor.setDate(cursor.getDate() + 1);
      }
      return startStr;
    };

    const dateStr = computeShiftDate();
    if (dateStr) {
      setReservationDetails((prev) => ({ ...prev, date: dateStr }));
      setBookingTypeChoice(isEventShift ? 'reservation' : 'regular');
      setDateViewMode('quick');
      // Pre-select the shift's label pill — for both regular and (now-visible)
      // event/Experience shifts opened via a direct link.
      setPendingShiftId(initialEventShift.id);
      setTimeout(() => {
        if (quickDatesContainerRef.current) {
          const pills = quickDatesContainerRef.current.querySelectorAll('.quick-date-pill');
          for (const pill of pills) {
            if (pill.classList.contains('active')) {
              pill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
              break;
            }
          }
        }
      }, 150);
    }
    if (onInitialEventShiftHandled) onInitialEventShiftHandled();
  }, [initialEventShift]);

  // Re-apply the deep-linked shift once that date's shifts have loaded. The
  // date-change effect clears selectedShiftId, so the desired id is held in
  // pendingShiftId until the matching shift is present, then selected.
  useEffect(() => {
    if (pendingShiftId == null || !shifts || shifts.length === 0) return;
    if (shifts.some((s) => s.id === pendingShiftId)) {
      setSelectedShiftId(pendingShiftId);
      setPendingShiftId(null);
    }
  }, [shifts, pendingShiftId]);

  // Auto-select first available date when blocked dates are loaded
  useEffect(() => {
    // Wait until blocked dates and experiences have been loaded from API (a
    // private experience can close a date too).
    if (!blockedDatesLoaded || !externalEventsLoaded) return;

    // Only auto-select if no date is currently selected
    if (reservationDetails.date) return;

    // Find the first available (non-blocked and non-sold-out) date
    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateValue = toLocalDateString(date);

      if (!isDateBlocked(dateValue) && !soldOutDates.includes(dateValue)) {
        setReservationDetails(prev => ({ ...prev, date: dateValue }));

        // Scroll to the selected date pill after a short delay
        setTimeout(() => {
          if (quickDatesContainerRef.current) {
            const datePills = quickDatesContainerRef.current.querySelectorAll('.quick-date-pill');
            if (datePills[i]) {
              datePills[i].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          }
        }, 100);
        break;
      }
    }
  }, [blockedDatesLoaded, blockedDates, soldOutDates, closedWeekdays, externalEventsLoaded, privateBlockedDates]);

  // When the Calendar View tab opens, jump the grid to the selected date's
  // month (or the current month if none selected yet).
  useEffect(() => {
    if (dateViewMode !== 'calendar') return;
    const base = reservationDetails.date
      ? new Date(`${reservationDetails.date}T12:00:00`)
      : new Date();
    setCalendarMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    setCalendarOpen(false); // grid starts collapsed; user clicks the field to open it
  }, [dateViewMode]);

  // Re-evaluate booking type when externalEvents load after date was already auto-selected
  useEffect(() => {
    if (!reservationDetails.date || externalEvents.length === 0) return;

    // If bookingTypeEvents are already set, skip (user already navigated dates)
    if (bookingTypeEvents.length > 0) return;

    // Skip for blocked/sold-out dates — they only show the unavailable message
    if (isDateBlocked(reservationDetails.date) || isDateSoldOut(reservationDetails.date)) return;

    const dateStr = reservationDetails.date;
    const blockingEvents = getBlockingEvents(dateStr);
    if (blockingEvents.length > 0) {
      setBookingTypeEvents(blockingEvents);
      setBookingTypeChoice('tickets');
      setSelectedTickets([]);
      setTicketTotal(0);
      if (blockingEvents.length === 1) {
        setSelectedEvent(blockingEvents[0]);
      } else {
        setSelectedEvent(null);
      }
      return;
    }

    const nonBlockingEvents = getNonBlockingEvents(dateStr);
    if (nonBlockingEvents.length > 0) {
      setBookingTypeEvents(nonBlockingEvents);
      // Default to the regular reservation form (shown beneath the experience
      // banner); the guest clicks the banner to switch to ticket booking.
      setBookingTypeChoice('regular');
    }
  }, [externalEvents, reservationDetails.date]);

  // Fetch shifts when date changes
  useEffect(() => {
    // New date → forget any previously chosen shift label so the guest re-picks.
    setSelectedShiftId(null);
    const fetchShifts = async () => {
      if (!reservationDetails.date || !restaurantId) {
        setShifts([]);
        setSlotAvailability({});
        return;
      }

      // Skip shift fetching for blocked or sold-out dates
      if (isDateBlocked(reservationDetails.date) || isDateSoldOut(reservationDetails.date)) {
        setShifts([]);
        setSlotAvailability({});
        return;
      }

      try {
        console.log('Fetching shifts for:', restaurantId, reservationDetails.date);
        const data = await getShiftsForDate(restaurantId, reservationDetails.date);
        console.log('Shifts response:', data);
        const fetchedShifts = data.shifts || [];
        setShifts(fetchedShifts);

        // Fetch slot availability for each shift
        if (fetchedShifts.length > 0) {
          const availabilityPromises = fetchedShifts.map(shift =>
            getSlotAvailability(shift.id, reservationDetails.date)
          );
          const availabilityResults = await Promise.all(availabilityPromises);

          // Create a map of shiftId -> availability data
          const availabilityMap = {};
          availabilityResults.forEach(result => {
            if (result && result.shiftId) {
              availabilityMap[result.shiftId] = result;
            }
          });
          setSlotAvailability(availabilityMap);
          console.log('Slot availability:', availabilityMap);
        } else {
          setSlotAvailability({});
        }
      } catch (err) {
        console.error('Error fetching shifts:', err);
        setShifts([]);
        setSlotAvailability({});
      }
    };
    fetchShifts();
  }, [restaurantId, reservationDetails.date]);

  // Clear selected time if it's no longer available (past time)
  useEffect(() => {
    if (reservationDetails.time && !timeSlots.includes(reservationDetails.time)) {
      handleStep1Change('time', '');
    }
  }, [reservationDetails.date, timeSlots]);

  // Note: blocked/sold-out dates are now clickable - they show a message instead of time slots

  // Clear selected time if guest count changes and slot no longer has enough capacity
  useEffect(() => {
    if (reservationDetails.time && Object.keys(slotAvailability).length > 0) {
      const slotInfo = getSlotInfo(reservationDetails.time);
      if (slotInfo.isBlocked || slotInfo.notEnoughCapacity) {
        handleStep1Change('time', '');
      }
    }
  }, [reservationDetails.guests, slotAvailability]);

  // Restore pending reservation after login
  useEffect(() => {
    if (isLoggedIn) {
      const pendingReservationStr = localStorage.getItem('pendingReservation');
      if (pendingReservationStr) {
        try {
          const pendingReservation = JSON.parse(pendingReservationStr);
          // Only restore if it's for the same restaurant
          if (pendingReservation.restaurantId === restaurantId) {
            setReservationDetails(pendingReservation.reservationDetails);
            setPersonalInfo(pendingReservation.personalInfo);
            if (pendingReservation.reservationDetails.specialOccasion || pendingReservation.celebrationSelection?.occasionId) {
              setHasSpecialOccasion(true);
            }
            if (pendingReservation.celebrationSelection) {
              setCelebrationSelection(pendingReservation.celebrationSelection);
            }
            if (pendingReservation.selectedZoneId) {
              setSelectedZoneId(pendingReservation.selectedZoneId);
            }
            setStep(2); // Go to step 2
            // Clear the pending reservation
            localStorage.removeItem('pendingReservation');
          }
        } catch (err) {
          console.error('Failed to restore pending reservation:', err);
          localStorage.removeItem('pendingReservation');
        }
      }
    }
  }, [isLoggedIn, restaurantId]);

  // Auto-fill personal info for logged-in users
  useEffect(() => {
    if (isLoggedIn && user) {
      setPersonalInfo(prev => ({
        ...prev,
        firstName: user.firstName || prev.firstName,
        lastName: user.lastName || prev.lastName,
        email: user.email || prev.email,
        phone: user.phoneNumber || prev.phone,
      }));
    }
  }, [isLoggedIn, user]);

  // Auto-fill from a host-provided profile (widget embeds where AuthContext can't
  // see the host's session). Only fills empty fields so it never clobbers typing.
  useEffect(() => {
    if (!prefillUser) return;
    setPersonalInfo(prev => ({
      ...prev,
      firstName: prev.firstName || prefillUser.firstName || '',
      lastName: prev.lastName || prefillUser.lastName || '',
      email: prev.email || prefillUser.email || '',
      phone: prev.phone || prefillUser.phone || '',
    }));
  }, [prefillUser]);


  const handleStep1Change = (field, value) => {
    setReservationDetails(prev => ({ ...prev, [field]: value }));
    if (value) setFieldErrors(prev => ({ ...prev, [field]: false }));

    // When date changes, check if new date has non-blocking events
    if (field === 'date') {
      // Skip booking type logic for blocked/sold-out dates — just show the message
      if (isDateBlocked(value) || isDateSoldOut(value)) {
        setBookingTypeChoice(null);
        setBookingTypeEvents([]);
      } else {
        const nonBlockingEvents = getNonBlockingEvents(value);
        if (nonBlockingEvents.length > 0) {
          // Date has non-blocking experience(s): default to the regular reservation
          // form (shown beneath the experience banner). Guest clicks the banner to
          // switch to ticket booking, and "Back to reservation" to come back.
          setBookingTypeEvents(nonBlockingEvents);
          setBookingTypeChoice('regular');
        } else {
          // No non-blocking events on this date - clear booking type state
          setBookingTypeChoice(null);
          setBookingTypeEvents([]);
        }
      }
    }
  };

  // Helper function to scroll to the selected time slot
  const scrollToSelectedSlot = (selectedTime) => {
    setTimeout(() => {
      if (timeSlotsContainerRef.current) {
        const slotButton = timeSlotsContainerRef.current.querySelector(`[data-time="${selectedTime}"]`);
        if (slotButton) {
          slotButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    }, 100);
  };

  // Handle time selection.
  //
  // Overnight shifts (e.g. 9 PM → 3 AM) use the "business day" model: a post-
  // midnight slot (2 AM) belongs to the day the shift STARTED, so the reservation
  // keeps the selected date and we do NOT bump it to the next calendar day. The
  // stored time is wall-clock (02:00) and the slot list already places late-night
  // slots after the evening ones (see sortTimeSlotsWithLateNightLast). This keeps
  // the booking, the table view and reports all grouped under the shift's night.
  const handleTimeSelection = (time) => {
    handleStep1Change('time', time);
  };

  const handleStep2Change = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    if (value) setFieldErrors(prev => ({ ...prev, [field]: false }));
    if (field === 'email' && value !== verifiedEmail) {
      setEmailVerified(false);
      setEmailOtpSent(false);
      setEmailOtp('');
      setEmailOtpError('');
    }
  };

  // Update a single guest's field in the per-guest data section.
  const handleGuestDetailChange = (index, field, value) => {
    // Once the booker edits Guest 1, stop auto-syncing it from the head data.
    if (index === 0) guestZeroTouchedRef.current = true;
    setGuestDetails(prev => {
      const next = [...prev];
      const current = next[index] || { firstName: '', lastName: '', email: '', phone: '', socialMediaLink: '' };
      next[index] = { ...current, [field]: value };
      return next;
    });
  };

  // Keep the per-guest list sized to the guest count, but only for shifts that
  // require all guests' data and for seat booking. Preserves entries already typed;
  // pads new slots empty and trims extras. No-op (and leaves state empty) for every
  // other shift, so the default booking behavior is completely unaffected.
  useEffect(() => {
    if (!collectAllGuests) return;
    const count = parseInt(reservationDetails.guests) || 0;
    setGuestDetails(prev => {
      if (prev.length === count) return prev;
      const next = [];
      for (let i = 0; i < count; i++) {
        next.push(prev[i] || { firstName: '', lastName: '', email: '', phone: '', socialMediaLink: '' });
      }
      return next;
    });
  }, [collectAllGuests, reservationDetails.guests, step]);

  // Prefill Guest 1 from the head/booker data so the lead guest isn't typed
  // twice. Stays in sync as the booker fills the head fields, and stops the
  // moment the booker edits Guest 1 themselves (guestZeroTouchedRef).
  useEffect(() => {
    if (!collectAllGuests) return;
    if (guestZeroTouchedRef.current) return;
    const head = {
      firstName: personalInfo.firstName || '',
      lastName: personalInfo.lastName || '',
      email: personalInfo.email || '',
      phone: personalInfo.phone || '',
      socialMediaLink: personalInfo.socialMediaLink || '',
    };
    setGuestDetails(prev => {
      if (prev.length === 0) return prev;
      const cur = prev[0] || {};
      if (
        cur.firstName === head.firstName &&
        cur.lastName === head.lastName &&
        cur.email === head.email &&
        cur.phone === head.phone &&
        cur.socialMediaLink === head.socialMediaLink
      ) {
        return prev; // already in sync — avoid a needless state update/loop
      }
      const next = [...prev];
      next[0] = { ...cur, ...head };
      return next;
    });
  }, [
    collectAllGuests,
    personalInfo.firstName,
    personalInfo.lastName,
    personalInfo.email,
    personalInfo.phone,
    personalInfo.socialMediaLink,
    guestDetails.length,
  ]);

  // Email OTP handlers (event tickets only)
  const handleEmailOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 1) {
      const newOtp = emailOtp.split('');
      newOtp[index] = value;
      const joined = newOtp.join('');
      setEmailOtp(joined);
      if (value && index < 5) {
        const nextInput = document.getElementById(`ticket-otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
      // Auto-submit the moment the last digit lands and all six are present —
      // no separate "Verify" tap needed.
      if (value && index === 5 && /^\d{6}$/.test(joined)) {
        handleEmailOtpSubmit(joined);
      }
    }
  };

  const handleEmailOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !emailOtp[index] && index > 0) {
      const prevInput = document.getElementById(`ticket-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleEmailOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setEmailOtp(pastedData);
    // Focus the last filled cell so the caret lands sensibly.
    const lastIdx = Math.min(pastedData.length, 6) - 1;
    if (lastIdx >= 0) {
      const el = document.getElementById(`ticket-otp-${lastIdx}`);
      if (el) el.focus();
    }
    // Paste the full code from the email → verify immediately (one tap total).
    if (pastedData.length === 6) {
      handleEmailOtpSubmit(pastedData);
    }
  };

  const handleEmailOtpSubmit = async (codeOverride) => {
    const code = typeof codeOverride === 'string' ? codeOverride : emailOtp;
    if (!code || code.length !== 6) {
      setEmailOtpError('Please enter a valid 6-digit code');
      return;
    }
    setEmailOtpLoading(true);
    setEmailOtpError('');
    try {
      await verifyGuestOtp(personalInfo.email, code);
      setEmailVerified(true);
      setVerifiedEmail(personalInfo.email);
      setEmailOtpSent(false);
      setError('');
      // Auto-advance to step 3
      initializeTicketHolders();
      setTicketStep(3);
      scrollToTop();
    } catch (err) {
      setEmailOtpError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleEmailOtpResend = async () => {
    setEmailOtpLoading(true);
    setEmailOtpError('');
    try {
      await resendGuestOtp(personalInfo.email);
      setEmailOtp('');
    } catch (err) {
      setEmailOtpError(err.response?.data?.message || 'Failed to resend code.');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const scrollToTop = () => {
    // Scroll our own window (works for the standalone widget page and the SPA).
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
    // When embedded in a host iframe our own window has no scrollbar — the host
    // sizes the frame to our full content height — so the scroll above is a no-op.
    // Ask the host to scroll the iframe back to the top so the user sees the error
    // banner on a failed step, and the top of the next step on a successful one.
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ source: 'tali-reservation', type: 'scrollToTop' }, '*');
      }
    } catch (e) {
      /* cross-origin parent — ignore */
    }
  };

  const validateStep1 = () => {
    const noParty = seatMode ? selectedSeatIds.length === 0 : !reservationDetails.guests;
    if (!reservationDetails.date || !reservationDetails.time || noParty) {
      setFieldErrors({
        date: !reservationDetails.date,
        time: !reservationDetails.time,
        guests: !reservationDetails.guests && !seatMode,
        seats: seatMode && selectedSeatIds.length === 0,
      });
      const missing = [];
      if (!reservationDetails.date) missing.push('a date');
      if (!reservationDetails.time) missing.push('a time');
      if (noParty) missing.push(seatMode ? 'your tables on the floor plan' : 'how many guests (Table for)');
      setError(`Please select ${missing.join(', ')}`);
      scrollToTop();
      return false;
    }
    // Seat booking: say who's coming (at least one person, at most one per chair
    // picked), and the group must meet the entrance rule (the API refuses it too).
    if (seatMode && (menCount + womenCount < 1 || menCount + womenCount > selectedSeatIds.length)) {
      setFieldErrors({ genderCounts: true });
      setError('Please tell us how many men and women are coming.');
      scrollToTop();
      return false;
    }
    if (seatMode && !getGenderRatio(menCount, womenCount, maxMenPercent).met) {
      setFieldErrors({ genderCounts: true });
      setError(`Your group doesn't meet the entrance rule: no more than ${maxMenPercent}% men. Change the men and women counts to continue.`);
      scrollToTop();
      return false;
    }
    if (hasSpecialOccasion && occasionsMode && !selectedOccasion) {
      setFieldErrors({ celebrationOccasion: true });
      setError('Please choose your occasion, or switch off Special Occasion.');
      scrollToTop();
      return false;
    }
    if (selectedOccasion && selectedOccasion.packages.length > 0 && !selectedCelebrationPackage) {
      setFieldErrors({ celebrationPackage: true });
      setError(`Please choose a ${selectedOccasion.name.toLowerCase()} package.`);
      scrollToTop();
      return false;
    }
    setFieldErrors({});
    // Check if selected date is blocked (shown to guests as sold out)
    if (isDateBlocked(reservationDetails.date)) {
      setError('The selected date is sold out. Please choose another date.');
      scrollToTop();
      return false;
    }
    // Check if selected date is sold out
    if (isDateSoldOut(reservationDetails.date)) {
      setError('The selected date is sold out. Please choose another date.');
      scrollToTop();
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    const { firstName, lastName, email, phone } = personalInfo;
    if (!firstName || !lastName || !email || !phone) {
      setFieldErrors({
        firstName: !firstName,
        lastName: !lastName,
        email: !email,
        phone: !phone,
      });
      const missing = [];
      if (!firstName) missing.push('first name');
      if (!lastName) missing.push('last name');
      if (!email) missing.push('email');
      if (!phone) missing.push('phone number');
      setError(`Please enter your ${missing.join(', ')}`);
      scrollToTop();
      return false;
    }
    setFieldErrors({});
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      scrollToTop();
      return false;
    }
    // Phone validation: must be a valid number for the selected country
    if (!isValidPhone(phone)) {
      setError('Please enter a valid phone number for the selected country');
      scrollToTop();
      return false;
    }
    // Social media link validation (only when the current shift asks for it)
    if (selectedShift?.requireSocialMedia) {
      const link = (personalInfo.socialMediaLink || '').trim();
      if (selectedShift.socialMediaRequired && !link) {
        setError('Please provide your social media link');
        scrollToTop();
        return false;
      }
      if (link) {
        try {
          new URL(link);
        } catch {
          setError('Please enter a valid social media URL (including https://)');
          scrollToTop();
          return false;
        }
      }
    }
    // Per-guest data validation (only when the selected shift asks for it, or seat booking).
    // First name, last name, email, and phone are required for every guest —
    // matching the event ticket-holder requirement. Seat booking only needs name
    // and email; phone is optional there but must be valid when given.
    if (collectAllGuests) {
      const count = parseInt(reservationDetails.guests) || 0;
      for (let i = 0; i < count; i++) {
        const g = guestDetails[i] || {};
        if (!g.firstName?.trim() || !g.lastName?.trim() || !g.email?.trim() || (!seatMode && !g.phone?.trim())) {
          setError(seatMode
            ? `Please fill in first name, last name, and email for guest ${i + 1}`
            : `Please fill in first name, last name, email, and phone for guest ${i + 1}`);
          scrollToTop();
          return false;
        }
        if (!emailRegex.test(g.email.trim())) {
          setError(`Please enter a valid email for guest ${i + 1}`);
          scrollToTop();
          return false;
        }
        if ((!seatMode || normPhoneKey(g.phone)) && !isValidPhone(g.phone)) {
          setError(`Please enter a valid phone number for guest ${i + 1}`);
          scrollToTop();
          return false;
        }
        // Per-guest social media link (only when the shift also asks for it).
        if (selectedShift?.requireSocialMedia) {
          const link = (g.socialMediaLink || '').trim();
          if (selectedShift.socialMediaRequired && !link) {
            setError(`Please provide a social media link for guest ${i + 1}`);
            scrollToTop();
            return false;
          }
          if (link) {
            try {
              new URL(link);
            } catch {
              setError(`Please enter a valid social media URL for guest ${i + 1} (including https://)`);
              scrollToTop();
              return false;
            }
          }
        }
      }
      // No copy-pasted guests: each guest needs a distinct email and phone.
      // (guestDetails[0] is the booker/head, so this also stops a guest from
      // reusing the booker's contact details.) Name is intentionally allowed to
      // repeat for regular reservations. Seat booking allows shared contact
      // details (a guest without an email can use the booker's).
      const people = guestDetails.slice(0, count);
      const dupError = seatMode ? null : findGuestDuplicateError(people, { checkName: false });
      if (dupError) {
        setError(dupError);
        scrollToTop();
        return false;
      }
      // A social media link can never be shared between guests.
      if (selectedShift?.requireSocialMedia) {
        const socialDupError = findSocialMediaDuplicateError(people);
        if (socialDupError) {
          setError(socialDupError);
          scrollToTop();
          return false;
        }
      }
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
      // Use setTimeout to scroll after React re-renders step 2
      setTimeout(() => {
        scrollToTop();
      }, 50);
    }
  };

  const handleLoginRedirect = () => {
    // Save current reservation details to localStorage so user can continue after login
    const reservationState = {
      restaurantId,
      restaurantName,
      reservationDetails,
      personalInfo,
      selectedZoneId,
      celebrationSelection,
    };
    localStorage.setItem('pendingReservation', JSON.stringify(reservationState));

    // Save current URL to return after login
    const returnUrl = window.location.pathname;
    localStorage.setItem('returnUrl', returnUrl);

    // Redirect to login page
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Seat booking: the map keeps refreshing during step 2 and may drop chairs
    // another guest paid for, so check the seats and counters again first.
    if (seatMode && !validateStep1()) {
      setStep(1);
      return;
    }
    if (!validateStep2()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // selectedShift (memoized above) determines shift-scoped fields on the payload.
      // Strip socialMediaLink from personalInfo spread — we only want to send it when the
      // shift explicitly asks for it, so it can't be submitted against an unrelated shift.
      const { socialMediaLink: personalInfoSocialMedia, ...personalInfoRest } = personalInfo;
      const trimmedSocialMedia = (personalInfoSocialMedia || '').trim();

      const payload = {
        restaurantId,
        ...reservationDetails,
        ...personalInfoRest,
        guests: parseInt(reservationDetails.guests),
        // Promo code for a regular reservation — re-validated server-side.
        ...(resPromo?.code && { promoCode: resPromo.code }),
        // Include preferred zone if selected
        ...(selectedZoneId && {
          preferredZoneId: selectedZoneId,
        }),
        // Include shift ID if the selected time falls within a shift
        ...(selectedShift && {
          shiftId: selectedShift.id,
        }),
        // Only attach socialMediaLink when the shift requires it and a value was provided
        ...(selectedShift?.requireSocialMedia && trimmedSocialMedia && {
          socialMediaLink: trimmedSocialMedia,
        }),
        // Per-guest data when the shift requires full data for every guest (and seat booking)
        ...(collectAllGuests && {
          guestDetails: guestDetails
            .slice(0, parseInt(reservationDetails.guests) || 0)
            .map(g => ({
              firstName: g.firstName,
              lastName: g.lastName,
              email: g.email,
              // Phone is optional for seat booking guests: leave out an empty one.
              ...(normPhoneKey(g.phone) && { phone: g.phone }),
              // Only attach a guest's social link when the shift asks for it and one was typed.
              ...(selectedShift?.requireSocialMedia && (g.socialMediaLink || '').trim() && {
                socialMediaLink: g.socialMediaLink.trim(),
              }),
            })),
        }),
        // Include payment currency if restaurant uses Paymob
        ...(restaurant?.paymentGateway === 'paymob' && {
          currency: selectedCurrency,
        }),
        // Chairs picked on the seat map and the men/women split (seat booking restaurants)
        ...(seatMode && { seatIds: selectedSeatIds, menCount, womenCount }),
        // Occasion picked from the list: staff see its name, plus anything the guest wrote
        ...(selectedOccasion && {
          specialOccasion: [selectedOccasion.name, reservationDetails.specialOccasion?.trim()]
            .filter(Boolean)
            .join(': ')
            .slice(0, 200),
        }),
        // Its package: ids only, the API prices it
        ...(selectedCelebrationPackage && {
          celebrationPackageId: selectedCelebrationPackage.id,
          celebrationAddonQuantity: selectedCelebrationPackage.addonName ? celebrationSelection.addonQuantity || 0 : 0,
        }),
      };

      console.log('Reservation payload:', payload);
      console.log('Selected shift:', selectedShift);

      // Use appropriate API based on login status
      const response = isLoggedIn
        ? await createReservationAsUser(payload)
        : await createReservation(payload);

      // Check if automatic payment is required (redirect to payment page)
      if (response.requiresPayment && response.paymentUrl) {
        // When the widget is embedded in a host page (Kiki's), break straight out
        // to the payment URL as a full standalone page — same as the email link —
        // so it scrolls on mobile. The in-site /payment iframe wrapper (used in the
        // normal site flow below) is a fixed-height, no-scroll container.
        let embedded = false;
        try {
          embedded = window.top !== window.self;
        } catch (e) {
          embedded = true;
        }
        if (embedded) {
          goToPaymentAbsolute(response.paymentUrl);
          return;
        }
        // Redirect to payment page with payment URL
        const params = new URLSearchParams({
          url: response.paymentUrl,
          reservationId: response.reservation.id.toString(),
          restaurantName: restaurantName,
        });
        goToPayment(`/payment?${params.toString()}`);
        return;
      }

      // Check if manual payment mode (admin will send payment link later)
      if (response.requiresPayment && response.paymentPending) {
        setSubmittedPaymentPending(true);
        setSuccess(true);
        setLoading(false);
        scrollToTop();
        return;
      }

      // Check if this went to waitlist
      if (response.isWaitlist) {
        setSubmittedToWaitlist(true);
        setWaitlistInfo({
          position: response.waitlistPosition,
          reason: response.waitlistReason,
        });
        setSuccess(true);
        setLoading(false);
        scrollToTop();
        return;
      }

      // Check if this was an approval-required slot
      const slotInfo = getSlotInfo(reservationDetails.time);
      setSubmittedRequiresApproval(slotInfo.requiresApproval || response.requiresApproval);

      // No payment required - show success
      setSuccess(true);
      setLoading(false);
      scrollToTop();
    } catch (err) {
      const rawMessage = err.response?.data?.message;
      const message = Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage;
      setError(message || 'Failed to create reservation. Please try again.');
      if (seatMode && err.response?.status === 409) {
        // A chair was taken while the guest filled in their details: back to the
        // seat map, keeping the picks that are still free (and the guest details
        // already typed for them). loadSeatMap drops only the chairs that are gone
        // — announce is off because the 409 message above already says which.
        setSeatNotice(message || 'Some of the seats you picked were just taken by another guest.');
        // The party may change with the chairs, so the split is stated again.
        setMenCount(0);
        setWomenCount(0);
        setStep(1);
        loadSeatMap({ announce: false });
      }
      setLoading(false);
      scrollToTop();
    }
  };

  const resetForm = () => {
    setStep(1);
    setSuccess(false);
    setSubmittedRequiresApproval(false);
    setSubmittedToWaitlist(false);
    setSubmittedPaymentPending(false);
    setWaitlistInfo({ position: null, reason: null });
    setReservationDetails({
      date: '',
      time: '',
      guests: '',
      specialOccasion: '',
      eventId: null,
    });
    setHasSpecialOccasion(false);
    setCelebrationSelection(EMPTY_CELEBRATION_SELECTION);
    setSelectedZoneId(null);
    setPersonalInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      allergies: [],
      dietaryPreference: [],
      comments: '',
      socialMediaLink: '',
    });
    setError('');
    // Reset custom guest input state
    setShowCustomGuestInput(false);
    setCustomGuestValue('');
    setSelectedSeatIds([]);
    setSeatNotice('');
    setMenCount(0);
    setWomenCount(0);
  };

  // When the guest is redirected OUT to the full-page payment link and then hits
  // the browser Back button, the phone restores this page from its back-forward
  // cache frozen exactly as it was left — mid-submit, so the button is stuck on
  // "Processing…" forever. The reservation was ALREADY created before that
  // redirect, so we must not let them re-submit the same form (it would create a
  // duplicate). Detect the restore (pageshow.persisted) and reset to a clean
  // step 1 so they start fresh.
  useEffect(() => {
    const onPageShow = (e) => {
      if (!e.persisted) return; // only true on a bfcache restore, not a normal load
      setLoading(false);
      setTicketMode(false);
      setTicketStep(1);
      resetForm();
      scrollToTop();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to format waitlist reason
  const formatWaitlistReason = (reason) => {
    switch (reason) {
      case 'max_guests_per_reservation':
        return 'Large party reservations require special approval.';
      case 'max_covers':
        return 'This time slot has reached its guest capacity.';
      case 'max_reservations':
        return 'This time slot has reached its reservation limit.';
      default:
        return 'This time slot is currently at capacity.';
    }
  };

  // Ticket submission success screen
  if (ticketSubmitSuccess) {
    return (
      <div className="reservation-success">
        <div className="success-icon">
          {ticketAutoApproved ? (
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '64px' }}></i>
          ) : selectedEvent?.requiresApproval ? (
            <i className="bi bi-shield-check text-info" style={{ fontSize: '64px' }}></i>
          ) : (
            <i className="bi bi-ticket-perforated text-success" style={{ fontSize: '64px' }}></i>
          )}
        </div>
        <h3 className="mt-3">
          {ticketAutoApproved
            ? 'Bookings Confirmed!'
            : selectedEvent?.requiresApproval
              ? 'Booking Request Submitted!'
              : 'Bookings Reserved Successfully!'}
        </h3>
        <p className="text-muted">
          {ticketAutoApproved
            ? <>Dear {personalInfo.firstName}, your bookings for <strong>{selectedEvent?.name}</strong> have been confirmed! Check your email for your QR codes.</>
            : <>Dear {personalInfo.firstName}, your booking for <strong>{selectedEvent?.name}</strong> has been submitted.
              {selectedEvent?.requiresApproval
                ? ' Our team will review your request and send you a payment link via email once approved.'
                : ' You will receive an email with further instructions.'}</>}
        </p>
        <div className="ticket-success-info-box">
          <i className="bi bi-envelope me-2"></i>
          <span>
            {ticketAutoApproved
              ? 'Your QR codes have been sent to your email.'
              : selectedEvent?.requiresApproval
                ? 'Check your email for approval status and payment link.'
                : 'A confirmation email has been sent to your inbox.'}
          </span>
        </div>
        <button
          className="btn btn-primary mt-3"
          onClick={singleEvent ? restartTicketBooking : exitTicketMode}
        >
          {singleEvent ? 'Make Another Booking' : 'Back to Reservations'}
        </button>
      </div>
    );
  }

  // Ticket mode UI
  if (ticketMode && selectedEvent) {
    return (
      <div className="reservation-form-container" ref={formContainerRef}>
        <div className="form-header">
          <h3>
            <i className="bi bi-ticket-perforated me-2"></i>
            Get Bookings
          </h3>
          <div className="step-indicator">
            <span className={ticketStep === 1 ? 'active' : ticketStep > 1 ? 'completed' : ''}>1</span>
            <div className="step-line"></div>
            <span className={ticketStep === 2 ? 'active' : ticketStep > 2 ? 'completed' : ''}>2</span>
            <div className="step-line"></div>
            <span className={ticketStep === 3 ? 'active' : ''}>3</span>
          </div>
        </div>

        {error && (
          <div className="error-alert" role="alert">
            {error}
          </div>
        )}

        {/* Step 1: Select Tickets */}
        {ticketStep === 1 && (
          <div className="step-content">
            <h4 className="step-title">Step 1: Select Bookings</h4>
            <EventTicketSelector
              event={selectedEvent}
              onSelectionChange={handleTicketSelectionChange}
              onBack={singleEvent ? null : exitTicketMode}
              restaurantName={restaurantName}
              hideEventHeader={singleEvent}
              extraContent={renderTicketOccasion()}
              extraLine={ticketPackageLine}
            />

            {selectedTickets.length > 0 && (
              <button
                type="button"
                className="btn btn-next mt-4"
                onClick={() => {
                  if (!checkTicketOccasion()) return;
                  setError('');
                  setTicketStep(2);
                  scrollToTop();
                }}
              >
                Continue to Your Details
              </button>
            )}
          </div>
        )}

        {/* Step 2: Buyer Information */}
        {ticketStep === 2 && (
          <div className="step-content step-2-content">
            <h4 className="step-title">Step 2: Your Information</h4>
            <p className="form-hint text-muted mb-3">
              We'll send your payment link and bookings to this email.
            </p>

            <div className="form-section">
              {/* Email — full width, first field, prominent */}
              <div className="ticket-email-field-wrapper mb-3">
                <label htmlFor="buyerEmail" className="form-label">
                  <i className="bi bi-envelope"></i> Email Address *
                </label>
                <div className="ticket-email-input-row">
                  <input
                    type="email"
                    className={`form-control modern-input ${emailVerified && personalInfo.email === verifiedEmail ? 'ticket-email-input-verified' : ''} ${fieldErrors.email ? 'is-invalid' : ''}`}
                    id="buyerEmail"
                    value={personalInfo.email}
                    onChange={(e) => handleStep2Change('email', e.target.value)}
                    placeholder="you@example.com"
                    required
                    readOnly={emailVerified && personalInfo.email === verifiedEmail}
                  />
                  {emailVerified && personalInfo.email === verifiedEmail && (
                    <span className="ticket-email-verified-badge">
                      <i className="bi bi-check-circle-fill"></i> Verified
                    </span>
                  )}
                </div>

                {/* OTP inline section */}
                {emailOtpSent && !emailVerified && (
                  <div className="ticket-otp-section">
                    <div className="ticket-otp-sent-to">
                      <i className="bi bi-envelope-check"></i>
                      <span>We sent a 6-digit code to <strong>{personalInfo.email}</strong></span>
                    </div>

                    <div className="ticket-otp-inputs">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          id={`ticket-otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={emailOtp[index] || ''}
                          onChange={(e) => handleEmailOtpChange(e, index)}
                          onKeyDown={(e) => handleEmailOtpKeyDown(e, index)}
                          onPaste={handleEmailOtpPaste}
                          className="ticket-otp-input"
                          autoComplete={index === 0 ? 'one-time-code' : 'off'}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>

                    {emailOtpError && (
                      <div className="ticket-otp-error">
                        <i className="bi bi-exclamation-circle"></i> {emailOtpError}
                      </div>
                    )}

                    <div className="ticket-otp-actions">
                      <button
                        type="button"
                        className="btn btn-next btn-primary-submit ticket-otp-verify-btn"
                        onClick={handleEmailOtpSubmit}
                        disabled={emailOtpLoading || emailOtp.length !== 6}
                      >
                        {emailOtpLoading ? (
                          <><span className="ticket-otp-spinner"></span> Verifying...</>
                        ) : (
                          <><i className="bi bi-shield-check"></i> Verify Email</>
                        )}
                      </button>
                      <button
                        type="button"
                        className="ticket-otp-resend-link"
                        onClick={handleEmailOtpResend}
                        disabled={emailOtpLoading}
                      >
                        Didn't receive it? <span>Resend Code</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Name row */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="buyerFirstName" className="form-label">First Name *</label>
                  <input
                    type="text"
                    className={`form-control modern-input ${fieldErrors.firstName ? 'is-invalid' : ''}`}
                    id="buyerFirstName"
                    value={personalInfo.firstName}
                    onChange={(e) => handleStep2Change('firstName', e.target.value)}
                    placeholder="First Name"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="buyerLastName" className="form-label">Last Name *</label>
                  <input
                    type="text"
                    className={`form-control modern-input ${fieldErrors.lastName ? 'is-invalid' : ''}`}
                    id="buyerLastName"
                    value={personalInfo.lastName}
                    onChange={(e) => handleStep2Change('lastName', e.target.value)}
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="mb-3">
                <label htmlFor="buyerPhone" className="form-label">Phone Number *</label>
                <PhoneInput
                  country={'eg'}
                  value={personalInfo.phone}
                  onChange={(phone) => handleStep2Change('phone', '+' + phone)}
                  inputProps={{ id: 'buyerPhone', required: true }}
                  containerClass="phone-input-container"
                  inputClass={`form-control modern-input ${fieldErrors.phone ? 'is-invalid' : ''}`}
                  buttonClass="phone-input-button"
                  enableSearch={true}
                  searchPlaceholder="Search country..."
                  preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-back"
                onClick={() => {
                  setTicketStep(1);
                  scrollToTop();
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-next btn-primary-submit"
                onClick={async () => {
                  if (!personalInfo.firstName || !personalInfo.lastName || !personalInfo.email || !personalInfo.phone) {
                    setError('Please fill in all required fields');
                    scrollToTop();
                    return;
                  }
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(personalInfo.email)) {
                    setError('Please enter a valid email address');
                    scrollToTop();
                    return;
                  }
                  if (!isValidPhone(personalInfo.phone)) {
                    setError('Please enter a valid phone number for the selected country');
                    scrollToTop();
                    return;
                  }
                  setError('');
                  // Logged-in user booking from the Kiki's embed: the host already
                  // authenticated them (host JWT → profile), so skip email OTP.
                  if (hostAuthenticated) {
                    initializeTicketHolders();
                    setTicketStep(3);
                    scrollToTop();
                    return;
                  }
                  // If already verified with same email, proceed
                  if (emailVerified && personalInfo.email === verifiedEmail) {
                    initializeTicketHolders();
                    setTicketStep(3);
                    scrollToTop();
                    return;
                  }
                  // Otherwise, send OTP
                  setEmailOtpLoading(true);
                  setEmailOtpError('');
                  try {
                    await sendGuestOtp(personalInfo.email);
                    setEmailOtpSent(true);
                    setEmailOtp('');
                  } catch (err) {
                    setEmailOtpError(err.response?.data?.message || 'Failed to send verification code.');
                  } finally {
                    setEmailOtpLoading(false);
                  }
                }}
                disabled={emailOtpLoading}
              >
                {emailOtpLoading ? 'Sending...' : 'Continue to Booking Holders'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Ticket Holder Information */}
        {ticketStep === 3 && (
          <div className="step-content step-3-content">
            <h4 className="step-title">Step 3: Booking Holder Details</h4>
            <p className="form-hint text-muted mb-3">
              Please provide details for each booking holder. Each person will receive their own QR code.
            </p>

            {selectedEvent?.requireSocialMedia && (() => {
              const total = ticketHolders.length;
              const filled = ticketHolders.filter(h => h.socialMediaLink?.trim()).length;
              const required = computeRequiredSocialMediaCount(total, selectedEvent.socialMediaCoverage);
              if (required <= 0) return null;
              const ok = filled >= required;
              const progress = Math.min(100, Math.round((filled / required) * 100));
              const accent = ok ? '#16a34a' : '#f59e0b';
              return (
                <div
                  className="mb-3"
                  style={{
                    background: '#fff',
                    border: `1px solid ${ok ? 'rgba(22, 163, 74, 0.25)' : 'rgba(245, 158, 11, 0.3)'}`,
                    borderRadius: 14,
                    padding: '14px 16px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: `${accent}1A`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-link-45deg'}`}
                        style={{ color: accent, fontSize: '1.1rem' }}
                      ></i>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.92rem' }}>
                          Social Media Links
                        </span>
                        <span style={{ fontSize: '0.85rem', color: accent, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {filled} / {required}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                        {ok
                          ? 'All set — thanks!'
                          : filled === 0
                            ? `Add ${required > 1 ? 'social media links' : 'a social media link'} for ${required} booking${required > 1 ? 's' : ''}`
                            : `Please add a link to ${required - filled} more booking${required - filled > 1 ? 's' : ''}`}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          height: 6,
                          borderRadius: 999,
                          background: 'rgba(0,0,0,0.06)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${progress}%`,
                            background: accent,
                            borderRadius: 999,
                            transition: 'width 0.25s ease',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {ticketHolders.length > 1 && selectedEvent?.allowSameAsBuyer !== false && (() => {
              const allOn = ticketHolders.every(h => h.sameAsBuyer);
              return (
                <div className="ticket-holders-bulk-actions mb-3">
                  <button
                    type="button"
                    className={`btn same-as-buyer-btn${allOn ? ' is-active' : ''}`}
                    aria-pressed={allOn}
                    onClick={applyBuyerInfoToAll}
                  >
                    {allOn && <i className="bi bi-check2 me-1"></i>}
                    Same as buyer for all bookings
                  </button>
                </div>
              );
            })()}

            {ticketHolders.map((holder, index) => (
              <div key={index} className="ticket-holder-card">
                <div className="ticket-holder-header">
                  <h5>
                    <i className="bi bi-ticket-perforated"></i>
                    Booking {index + 1}: {holder.ticketTypeName}
                  </h5>
                  {selectedEvent?.allowSameAsBuyer !== false && (
                    <button
                      type="button"
                      id={`sameAsBuyer-${index}`}
                      className={`btn same-as-buyer-btn${holder.sameAsBuyer ? ' is-active' : ''}`}
                      aria-pressed={holder.sameAsBuyer}
                      onClick={() => handleTicketHolderChange(index, 'sameAsBuyer', !holder.sameAsBuyer)}
                    >
                      {holder.sameAsBuyer && <i className="bi bi-check2 me-1"></i>}
                      Same as buyer
                    </button>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      className="form-control modern-input"
                      value={holder.firstName}
                      onChange={(e) => handleTicketHolderChange(index, 'firstName', e.target.value)}
                      placeholder="First Name"
                      disabled={holder.sameAsBuyer}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      className="form-control modern-input"
                      value={holder.lastName}
                      onChange={(e) => handleTicketHolderChange(index, 'lastName', e.target.value)}
                      placeholder="Last Name"
                      disabled={holder.sameAsBuyer}
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-control modern-input"
                      value={holder.email}
                      onChange={(e) => handleTicketHolderChange(index, 'email', e.target.value)}
                      placeholder="Email Address"
                      disabled={holder.sameAsBuyer}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Phone *</label>
                    <PhoneInput
                      country={'eg'}
                      value={holder.phone}
                      onChange={(phone) => handleTicketHolderChange(index, 'phone', '+' + phone)}
                      disabled={holder.sameAsBuyer}
                      containerClass="phone-input-container"
                      inputClass="form-control modern-input"
                      buttonClass="phone-input-button"
                      enableSearch={true}
                      searchPlaceholder="Search country..."
                      preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
                    />
                  </div>
                </div>
                {selectedEvent?.requireSocialMedia && (
                  <div className="row">
                    <div className="col-12 mb-3">
                      <label className="form-label">
                        <i className="bi bi-link-45deg me-1"></i>
                        Social Media Link
                        {selectedEvent.socialMediaCoverage === 'all' && <span className="text-danger"> *</span>}
                      </label>
                      <input
                        type="url"
                        className="form-control modern-input"
                        value={holder.socialMediaLink}
                        onChange={(e) => handleTicketHolderChange(index, 'socialMediaLink', e.target.value)}
                        placeholder="Instagram, Facebook, or other profile link"
                        required={selectedEvent.socialMediaCoverage === 'all'}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Order Summary (the Special Occasion package was picked on the first screen) */}
            <div className="ticket-order-summary">
              <h5>Order Summary</h5>
              {selectedTickets.map(ticket => (
                <div key={ticket.ticketTypeId} className="summary-item">
                  <span>{ticket.quantity}x {ticket.ticketTypeName}</span>
                  <span>EGP {((ticket.priceCents / 100) * ticket.quantity * (ticket.priceExcludesVat ? 1.14 : 1)).toFixed(2)}</span>
                </div>
              ))}

              {/* Promo Code */}
              <div className="summary-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, paddingTop: 10, borderTop: '1px dashed #e5e7eb', marginTop: 6 }}>
                {!promo && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Promo code (optional)"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value); setPromoError(''); }}
                      disabled={promoLoading}
                      style={{ flex: 1 }}
                      onKeyDown={async (e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const code = promoInput.trim();
                        if (!code) return;
                        setPromoLoading(true); setPromoError('');
                        try {
                          const ticketTypeIds = selectedTickets.flatMap(t => Array(t.quantity).fill(t.ticketTypeId));
                          const result = await validatePromoCodePublic({ code, eventId: selectedEvent.id, ticketTypeIds, buyerEmail: personalInfo.email });
                          if (!result?.valid || !result.totalDiscountCents) {
                            setPromoError('Promo code does not apply to your bookings'); setPromo(null);
                          } else {
                            setPromo({ code: result.code, discountPercent: result.discountPercent, perItem: result.perItem || [] });
                          }
                        } catch (err) {
                          const raw = err.response?.data?.message;
                          setPromoError(Array.isArray(raw) ? raw.join(', ') : (typeof raw === 'string' ? raw : 'Could not validate promo code'));
                          setPromo(null);
                        } finally { setPromoLoading(false); }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-dark"
                      disabled={promoLoading || !promoInput.trim()}
                      onClick={async () => {
                        const code = promoInput.trim();
                        if (!code) return;
                        setPromoLoading(true); setPromoError('');
                        try {
                          const ticketTypeIds = selectedTickets.flatMap(t => Array(t.quantity).fill(t.ticketTypeId));
                          const result = await validatePromoCodePublic({ code, eventId: selectedEvent.id, ticketTypeIds, buyerEmail: personalInfo.email });
                          if (!result?.valid || !result.totalDiscountCents) {
                            setPromoError('Promo code does not apply to your bookings'); setPromo(null);
                          } else {
                            setPromo({ code: result.code, discountPercent: result.discountPercent, perItem: result.perItem || [] });
                          }
                        } catch (err) {
                          const raw = err.response?.data?.message;
                          setPromoError(Array.isArray(raw) ? raw.join(', ') : (typeof raw === 'string' ? raw : 'Could not validate promo code'));
                          setPromo(null);
                        } finally { setPromoLoading(false); }
                      }}
                    >
                      {promoLoading ? <span className="spinner-border spinner-border-sm" /> : 'Apply'}
                    </button>
                  </div>
                )}
                {promo && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
                    <span><strong>{promo.code}</strong> · {promo.discountPercent}% off applied</span>
                    <button type="button" className="btn btn-link btn-sm text-danger" style={{ padding: 0 }} onClick={() => { setPromo(null); setPromoInput(''); setPromoError(''); }}>Remove</button>
                  </div>
                )}
                {promoError && (
                  <div style={{ color: '#b91c1c', fontSize: 12 }}>
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    {promoError}
                  </div>
                )}
              </div>

              {(() => {
                const subtotalCents = selectedTickets.reduce((sum, t) => sum + t.priceCents * t.quantity, 0);
                const totalTicketCount = selectedTickets.reduce((sum, t) => sum + t.quantity, 0);
                const feeCents = ticketFeeConfig.fixedFeeCents * totalTicketCount + Math.round(subtotalCents * ticketFeeConfig.feePercent / 100);
                // Promo discount applies to ticket prices only (matches backend math).
                const discountCents = promo?.perItem
                  ? selectedTickets.reduce((sum, t) => {
                      const plan = promo.perItem.find(p => p.ticketTypeId === t.ticketTypeId);
                      return sum + (plan ? plan.discountCents * t.quantity : 0);
                    }, 0)
                  : 0;
                // Occasion package: paid with the tickets; the fee % applies to it too
                const packageCents = ticketPackageLine?.cents || 0;
                const packageFeeCents = ticketPackageLine?.feeCents || 0;
                const hasFee = feeCents + packageFeeCents > 0;
                const totalCents = subtotalCents + feeCents + packageCents + packageFeeCents - discountCents;
                return (
                  <>
                    {packageCents > 0 && (
                      <div className="summary-item">
                        <span>
                          <i className="bi bi-gift me-1"></i>
                          {ticketPackageLine.label}
                        </span>
                        <span>EGP {(packageCents / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {hasFee && (
                      <div className="summary-item fee-line">
                        <span>Service Fee</span>
                        <span>EGP {((feeCents + packageFeeCents) / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {discountCents > 0 && (
                      <div className="summary-item fee-line" style={{ color: '#166534' }}>
                        <span>Promo discount</span>
                        <span>− EGP {(discountCents / 100).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-total">
                      <span>Total</span>
                      <span>EGP {(Math.max(0, totalCents) / 100).toFixed(2)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Authorize & Capture: tell the guest before they submit */}
            {selectedEvent?.paymentMode === 'authorize_capture' && (
              <div className="alert alert-info d-flex align-items-start mt-3 mb-0" style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-shield-lock me-2 mt-1"></i>
                <span>
                  After submitting you will pay the full amount, but it is only held on your card. The venue
                  then reviews your booking: the amount is taken once it is approved, and released if it is not.
                </span>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-back"
                onClick={() => {
                  setTicketStep(2);
                  scrollToTop();
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-submit btn-primary-submit"
                onClick={() => {
                  // Validate all ticket holders
                  const total = ticketHolders.length;
                  const filledSocial = ticketHolders.filter(h => h.socialMediaLink?.trim()).length;
                  const requiredSocial = selectedEvent?.requireSocialMedia
                    ? computeRequiredSocialMediaCount(total, selectedEvent.socialMediaCoverage)
                    : 0;
                  const missingContactInfo = ticketHolders.some(h => !h.firstName || !h.lastName || !h.email);
                  if (missingContactInfo) {
                    setError('Please fill in first name, last name, and email for each booking holder.');
                    scrollToTop();
                    return;
                  }
                  // Phone is optional per holder, but when provided it must be a
                  // valid number for the selected country. "Same as buyer" holders
                  // inherit the buyer's already-validated phone, so skip those.
                  const badPhoneIdx = ticketHolders.findIndex(h => !h.sameAsBuyer && h.phone?.trim() && !isValidPhone(h.phone));
                  if (badPhoneIdx !== -1) {
                    setError(`Please enter a valid phone number for booking holder ${badPhoneIdx + 1}.`);
                    scrollToTop();
                    return;
                  }
                  // When "Same as Buyer" is disabled, the restaurant wants each
                  // guest's real details — so block copy-pasted name/email/phone.
                  if (selectedEvent?.allowSameAsBuyer === false) {
                    const dupError = findGuestDuplicateError(ticketHolders, { checkName: true });
                    if (dupError) {
                      setError(dupError);
                      scrollToTop();
                      return;
                    }
                  }
                  // A social media link can never be shared between guests.
                  if (selectedEvent?.requireSocialMedia) {
                    const socialDupError = findSocialMediaDuplicateError(ticketHolders);
                    if (socialDupError) {
                      setError(socialDupError);
                      scrollToTop();
                      return;
                    }
                  }
                  if (filledSocial < requiredSocial) {
                    const missing = requiredSocial - filledSocial;
                    setError(`Please add a social media link to ${missing} more booking${missing > 1 ? 's' : ''} (${filledSocial}/${requiredSocial} provided).`);
                    scrollToTop();
                    return;
                  }
                  setError('');
                  handleTicketSubmit();
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Booking'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (success) {
    return (
      <div className="reservation-success">
        <div className="success-icon">
          {submittedToWaitlist ? (
            <i className="bi bi-hourglass-split text-warning" style={{ fontSize: '64px' }}></i>
          ) : submittedRequiresApproval ? (
            <i className="bi bi-shield-check text-info" style={{ fontSize: '64px' }}></i>
          ) : submittedPaymentPending ? (
            <i className="bi bi-credit-card text-primary" style={{ fontSize: '64px' }}></i>
          ) : (
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '64px' }}></i>
          )}
        </div>
        {submittedToWaitlist ? (
          <>
            <h3 className="mt-3">You're on the Waitlist!</h3>
            <div className="waitlist-position-badge">
              #{waitlistInfo.position}
            </div>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your reservation has been added to the waitlist.
              {formatWaitlistReason(waitlistInfo.reason)}
            </p>
            <div className="waitlist-info-box">
              <i className="bi bi-info-circle me-2"></i>
              <span>We'll notify you by email as soon as a spot becomes available. If approved, you'll receive instructions to complete your booking.</span>
            </div>
          </>
        ) : submittedRequiresApproval ? (
          <>
            <h3 className="mt-3">Reservation Request Submitted!</h3>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your reservation request has been submitted for review.
              The restaurant team will review your request and get back to you via email shortly.
            </p>
            <div className="approval-info-box">
              <i className="bi bi-info-circle me-2"></i>
              <span>This time slot requires approval. You will receive a confirmation email once your request is approved.</span>
            </div>
          </>
        ) : submittedPaymentPending ? (
          <>
            <h3 className="mt-3">Reservation Submitted!</h3>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your reservation has been submitted successfully.
              Our team will contact you regarding payment details.
            </p>
            <div className="payment-pending-info-box">
              <i className="bi bi-credit-card me-2"></i>
              <span>A payment link will be sent to your email or phone. Your reservation will be confirmed once payment is complete.</span>
            </div>
          </>
        ) : (
          <>
            <h3 className="mt-3">Reservation Request Successful!</h3>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your request is successful! Someone from the team will get back to you via email or WhatsApp.
            </p>
          </>
        )}
        <button className="btn btn-primary mt-3" onClick={resetForm}>
          Make Another Reservation
        </button>
      </div>
    );
  }

  return (
    <div className="reservation-form-container" ref={formContainerRef}>
      {/* Event Popup - Show when user clicks on an event */}
      {showEventPopup && externalEvents.length > 0 && (
        <EventPopup
          events={externalEvents}
          initialIndex={selectedEventIndex}
          onClose={() => setShowEventPopup(false)}
        />
      )}

      <div className="form-header">
        <h3>
          <i className="bi bi-calendar-check me-2"></i>
          Reserve at {restaurantName}
        </h3>
        <div className="step-indicator">
          <span className={step === 1 ? 'active' : step > 1 ? 'completed' : ''}>1</span>
          <div className="step-line"></div>
          <span className={step === 2 ? 'active' : ''}>2</span>
        </div>
      </div>

      {error && (
        <div className="error-alert" role="alert">
          {error}
        </div>
      )}

      {step === 1 && (
        <div className="step-content step-1-content">
          <h4 className="step-title">Step 1: Reservation Details</h4>

          {/* Date Selection — hidden on the dedicated single-experience page,
              where the date is fixed to the experience. */}
          <div className="form-section" style={singleEvent ? { display: 'none' } : undefined}>
            <div className="section-header">
              <label className={`form-label ${fieldErrors.date ? 'text-danger fw-bold' : ''}`}>Select a date: {fieldErrors.date && <span className="text-danger">(required)</span>}</label>
              <div className="view-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${dateViewMode === 'quick' ? 'active' : ''}`}
                  onClick={() => setDateViewMode('quick')}
                >
                  Quick Date
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${dateViewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setDateViewMode('calendar')}
                >
                  Calendar View
                </button>
              </div>
            </div>

            {/* Blocked Dates Message */}
            {allQuickDatesBlocked() && nextAvailableDate && (
              <div className="blocked-dates-message">
                <i className="bi bi-calendar-x me-2"></i>
                <span>
                  We will start accepting reservations from <strong>{new Date(nextAvailableDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</strong>. Please use the calendar view to select a date.
                </span>
              </div>
            )}

            {dateViewMode === 'quick' ? (
              <div className="quick-dates-scroll">
                <button className="scroll-btn left" onClick={(e) => {
                  e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="quick-dates-container" ref={quickDatesContainerRef}>
                  {getQuickDates().map((d, idx) => (
                    <button
                      key={idx}
                      type="button"
                      // A blocked / closed date looks exactly like a sold-out one
                      className={`quick-date-pill ${reservationDetails.date === d.value ? 'active' : ''} ${d.isSoldOut || d.isBlocked ? 'sold-out' : ''} ${d.hasEvent ? 'has-event' : ''}`}
                      onClick={() => {
                        if (d.hasEvent) {
                          handleEventBadgeClick({ stopPropagation: () => {} }, d.value);
                        } else {
                          handleStep1Change('date', d.value);
                        }
                      }}
                      title={d.isSoldOut || d.isBlocked ? 'Sold out - no availability' : ''}
                    >
                      {d.hasEvent && (
                        <div className="event-badge" onClick={(e) => handleEventBadgeClick(e, d.value)}>
                          <i className="bi bi-calendar-event-fill"></i>
                          <span>Experience</span>
                        </div>
                      )}
                      <div className="weekday">{d.weekday}</div>
                      <div className="date">{d.monthDay}</div>
                      {(d.isSoldOut || d.isBlocked) && (
                        <div className="sold-out-badge">
                          <i className="bi bi-x-circle-fill"></i>
                          <span>SOLD OUT</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <button className="scroll-btn right" onClick={(e) => {
                  e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            ) : (
              <div className="calendar-view">
                <button
                  type="button"
                  className={`calendar-field ${calendarOpen ? 'open' : ''}`}
                  onClick={() => setCalendarOpen(o => !o)}
                  aria-expanded={calendarOpen}
                >
                  <span className="calendar-field-value">
                    <i className="bi bi-calendar3 me-2"></i>
                    {reservationDetails.date
                      ? new Date(`${reservationDetails.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                      : 'Select a date'}
                  </span>
                  <i className={`bi bi-chevron-${calendarOpen ? 'up' : 'down'} calendar-field-caret`}></i>
                </button>
                {calendarOpen && (() => {
                  const todayStr = toLocalDateString(new Date());
                  const currentMonthStart = (() => {
                    const n = new Date();
                    return new Date(n.getFullYear(), n.getMonth(), 1);
                  })();
                  const prevDisabled = calendarMonth <= currentMonthStart;
                  return (
                    <div className="calendar-grid-wrapper">
                      <div className="calendar-grid-header">
                        <button
                          type="button"
                          className="calendar-nav-btn"
                          onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                          disabled={prevDisabled}
                          aria-label="Previous month"
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="calendar-month-label">
                          {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button
                          type="button"
                          className="calendar-nav-btn"
                          onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                          aria-label="Next month"
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </div>
                      <div className="calendar-weekdays">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(w => (
                          <div key={w} className="calendar-weekday">{w}</div>
                        ))}
                      </div>
                      <div className="calendar-days-grid">
                        {buildCalendarDays(calendarMonth).map((day, idx) => {
                          if (!day) return <div key={`empty-${idx}`} className="calendar-day-cell empty" />;
                          const dateStr = toLocalDateString(day);
                          const isPast = dateStr < todayStr;
                          const blocked = isDateBlocked(dateStr);
                          const soldOut = isDateSoldOut(dateStr);
                          const dayEvents = getEventsForDate(dateStr);
                          const hasEvent = dayEvents.length > 0;
                          const isSelected = reservationDetails.date === dateStr;
                          return (
                            <button
                              key={dateStr}
                              type="button"
                              className={`calendar-day-cell ${isSelected ? 'selected' : ''} ${hasEvent ? 'has-event' : ''} ${soldOut || blocked ? 'sold-out' : ''} ${isPast ? 'past' : ''}`}
                              disabled={isPast}
                              title={hasEvent ? dayEvents.map(e => e.title).filter(Boolean).join(', ') : ''}
                              onClick={() => selectCalendarDate(dateStr)}
                            >
                              <span className="calendar-day-number">{day.getDate()}</span>
                              {hasEvent && <span className="calendar-event-dot" />}
                            </button>
                          );
                        })}
                      </div>
                      <div className="calendar-legend">
                        <span className="calendar-legend-item">
                          <span className="calendar-legend-swatch event" /> Experience
                        </span>
                        <span className="calendar-legend-item">
                          <span className="calendar-legend-swatch soldout" /> Sold out
                        </span>
                      </div>
                    </div>
                  );
                })()}
                {calendarToast && (
                  <div className="calendar-popup-overlay" onClick={() => setCalendarToast(null)}>
                    <div className={`calendar-popup calendar-popup-${calendarToast.type}`} onClick={(e) => e.stopPropagation()}>
                      <div className="calendar-popup-icon">
                        <i className={`bi ${calendarToast.type === 'soldout' ? 'bi-bag-x-fill' : calendarToast.type === 'event' ? 'bi-calendar-event-fill' : 'bi-lock-fill'}`}></i>
                      </div>
                      <div className="calendar-popup-date">
                        {calendarToast.dayName}, {calendarToast.formattedDate}
                      </div>
                      <div className="calendar-popup-message">{calendarToast.message}</div>
                      <button className="calendar-popup-btn" onClick={() => setCalendarToast(null)}>
                        OK
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Booking Type Selection - Show when date has events */}
          {reservationDetails.date && bookingTypeEvents.length > 0 && !isDateSoldOut(reservationDetails.date) && (() => {
            const isBlocking = bookingTypeEvents.some(e => e.blocksRegularReservations);
            const blockingEvent = bookingTypeEvents.find(e => e.blocksRegularReservations);
            // "Book from Widget" off ⇒ the experience isn't booked inside the widget;
            // its banner instead links out to the dedicated /experience/:id page.
            // It affects ONLY experience booking — regular reservations still follow
            // the Block-Reservations setting. On the dedicated single-experience page
            // (singleEvent) the experience is ALWAYS bookable — that page exists
            // precisely to book it — so bookFromWidget must not gate it there.
            const blockingBookable = singleEvent
              ? true
              : (blockingEvent ? blockingEvent.bookFromWidget !== false : false);
            const announcementOnly = isBlocking && !blockingBookable;
            const showTicketDetails = bookingTypeChoice === 'tickets' && !announcementOnly;

            const renderBanner = (event) => {
              // bookFromWidget ON  → book inside the widget (open ticket selection here).
              // bookFromWidget OFF → link the guest out to the dedicated full-page
              //   booking page (/experience/:id), which works like the standalone
              //   KIKI'S page. (On the dedicated page itself this gating is moot —
              //   singleEvent forces ticket mode, so renderBanner isn't reached.)
              const bookInWidget = event.bookFromWidget !== false;
              const linksToPage = !bookInWidget && !singleEvent;
              const showCta = bookInWidget || linksToPage;
              const inner = (
                <>
                  {event.imageUrl && (
                    <img
                      src={event.imageUrl}
                      alt={event.title}
                      className="experience-banner-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="experience-banner-body">
                    <span className="experience-banner-badge">
                      <i className="bi bi-ticket-perforated me-1"></i>Experience
                    </span>
                    <span className="experience-banner-title">{event.title}</span>
                    {event.time && (
                      <span className="experience-banner-time">
                        <i className="bi bi-clock me-1"></i>{event.time}
                      </span>
                    )}
                    {showCta && (
                      <span className="experience-banner-cta">
                        View experience <i className="bi bi-chevron-right"></i>
                      </span>
                    )}
                  </div>
                </>
              );
              // Open the dedicated booking page in the SAME tab. The widget often
              // runs inside a cross-origin iframe on the restaurant's site, so when
              // framed we navigate the TOP window (to this widget's own Tali origin,
              // built from window.location.origin) — that shows the experience page
              // full-screen in the same tab instead of inside the small frame.
              // Standalone (or if top-navigation is blocked) we navigate in place.
              const openDedicatedPage = () => openEventDedicatedPage(event);
              if (bookInWidget) {
                return (
                  <button
                    key={event.id}
                    type="button"
                    className="experience-banner"
                    onClick={() => handleSelectEventTickets(event)}
                  >
                    {inner}
                  </button>
                );
              }
              if (linksToPage) {
                return (
                  <button
                    key={event.id}
                    type="button"
                    className="experience-banner"
                    onClick={openDedicatedPage}
                  >
                    {inner}
                  </button>
                );
              }
              return (
                <div key={event.id} className="experience-banner experience-banner-static">
                  {inner}
                </div>
              );
            };

            return (
            <div className={`form-section ${showTicketDetails ? 'booking-type-section' : 'experience-banner-section'}`}>
              {announcementOnly ? (
                /* Display-only experience (blocks reservations + not bookable from
                   the widget): show the banner, nothing else. */
                <div className="experience-banners">
                  {bookingTypeEvents.map(renderBanner)}
                </div>
              ) : isBlocking ? (
                /* The dedicated page renders its own event hero above the form, so
                   skip this duplicate name label there. */
                singleEvent ? null : (
                  <label className="form-label" style={{ textAlign: 'center', display: 'block' }}>
                    <i className="bi bi-ticket-perforated me-2"></i>
                    {blockingEvent?.name || 'Experience'}
                  </label>
                )
              ) : bookingTypeChoice === 'tickets' ? (
                /* Viewing experience details — offer a way back to the regular form.
                   Not on the dedicated single-experience page: there's no regular
                   reservation context to return to there. */
                singleEvent ? null : (
                  <button
                    type="button"
                    className="btn btn-link p-0 mb-1 text-decoration-none experience-back-btn"
                    onClick={() => {
                      setBookingTypeChoice('regular');
                      setSelectedEvent(null);
                      setSelectedTickets([]);
                      setTicketTotal(0);
                    }}
                  >
                    <i className="bi bi-arrow-left me-1"></i>Back to reservation
                  </button>
                )
              ) : (
                /* Default on a non-blocking experience date: experience banner(s)
                   on top (clickable only when bookable from the widget); the regular
                   reservation form renders below. */
                <div className="experience-banners">
                  {bookingTypeEvents.map(renderBanner)}
                </div>
              )}

              {/* Event Selection - Show when tickets selected and multiple events exist */}
              {showTicketDetails && bookingTypeEvents.length > 1 && !selectedEvent && (
                <div className="booking-type-events-list mt-3">
                  <p className="form-hint text-muted mb-2">Select an experience:</p>
                  {bookingTypeEvents.filter(e => e.bookFromWidget !== false).map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      className={`booking-type-event-item ${selectedEvent?.id === event.id ? 'active' : ''}`}
                      onClick={() => handleSelectEventTickets(event)}
                    >
                      {event.imageUrl && (
                        <img
                          src={event.imageUrl}
                          alt={event.title}
                          className="booking-type-event-thumb"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="booking-type-event-info">
                        <h5>{event.title}</h5>
                        {event.time && <span className="booking-type-event-time"><i className="bi bi-clock me-1"></i>{event.time}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Inline Event Ticket Selector - Show when user chose event tickets */}
              {showTicketDetails && selectedEvent && (
                <div className="inline-ticket-selector">
                  {bookingTypeEvents.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-link p-0 mb-2 text-decoration-none"
                      onClick={() => {
                        setSelectedEvent(null);
                        setSelectedTickets([]);
                        setTicketTotal(0);
                      }}
                    >
                      <i className="bi bi-arrow-left me-1"></i>Choose a different experience
                    </button>
                  )}
                  <EventTicketSelector
                    event={selectedEvent}
                    onSelectionChange={handleTicketSelectionChange}
                    restaurantName={restaurantName}
                    hideEventHeader={singleEvent}
                    extraContent={renderTicketOccasion()}
                    extraLine={ticketPackageLine}
                  />

                  {selectedTickets.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-next mt-3"
                      onClick={() => {
                        if (!checkTicketOccasion()) return;
                        setError('');
                        // Enter full ticket mode for buyer info
                        setTicketMode(true);
                        setTicketStep(2);
                        scrollToTop();
                      }}
                    >
                      Continue to Your Details
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {/* Sold Out Date Message — replaces time slots. A blocked / closed date
              shows the same message as a sold-out one. */}
          {reservationDetails.date && (isDateSoldOut(reservationDetails.date) || isDateBlocked(reservationDetails.date)) && (
            <div className="form-section">
              <div className="date-unavailable-message sold-out">
                <i className="bi bi-bag-x-fill"></i>
                <div>
                  <strong>Sold Out</strong>
                  <p>This date is fully booked. Please select another date.</p>
                </div>
              </div>
            </div>
          )}

          {/* Time Selection - Only show when no event choice needed OR user chose regular reservation */}
          {reservationDetails.date && !isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
          <div className="form-section">
            <label className={`form-label ${fieldErrors.time ? 'text-danger fw-bold' : ''}`}>Select a time: {fieldErrors.time && <span className="text-danger">(required)</span>}</label>
            <p className="form-hint text-muted">
              {!reservationDetails.date
                ? 'Please select a date first'
                : mustChooseShift
                  ? 'Please choose an option below to see the available times'
                  : 'Select your preferred time slot'}
            </p>

            {/* Visible Shifts Selection - Show shift pills when shifts have showLabelToCustomers enabled */}
            {visibleShifts.length > 0 && reservationDetails.date && (
              <div className="shifts-selection mb-3">
                <div className="shifts-pills-container">
                  {visibleShifts.map(shift => {
                    const soldOut = isShiftSoldOutOnDate(shift);
                    return (
                    <button
                      key={shift.id}
                      type="button"
                      // Sold-out shifts stay visible so guests can see the option
                      // exists, but they cannot be picked on that date.
                      disabled={soldOut}
                      aria-disabled={soldOut}
                      className={`shift-pill ${
                        activeLabeledShift && activeLabeledShift.id === shift.id ? 'active' : ''
                      } ${soldOut ? 'sold-out' : ''}`}
                      onClick={() => {
                        if (soldOut) return;
                        // Pick this shift; only its time slots will show. Clear any time
                        // chosen under a different shift so it isn't carried over.
                        setSelectedShiftId(shift.id);
                        handleStep1Change('time', '');
                      }}
                    >
                      <span className="shift-label">{shift.label}</span>
                      <span className="shift-time">
                        {formatTime(shift.shiftStartTime)} - {formatTime(shift.shiftEndTime)}
                      </span>
                      {soldOut && <span className="shift-soldout-tag">Sold Out</span>}
                    </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Experience (event) shift banner — image + description for the picked
                event-type shift, so its visuals show in the widget. */}
            {activeLabeledShift && activeLabeledShift.shiftType === 'event' && (activeLabeledShift.imageUrl || activeLabeledShift.description) && (
              <div className="mb-3">
                {activeLabeledShift.imageUrl && (
                  <div style={{ width: '100%', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 15px rgba(26, 26, 26, 0.12)' }}>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ''}${activeLabeledShift.imageUrl}`}
                      alt={activeLabeledShift.label}
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                {activeLabeledShift.description && (
                  <p style={{ whiteSpace: 'pre-wrap', color: '#444', margin: '10px 0 0' }}>{activeLabeledShift.description}</p>
                )}
              </div>
            )}

            {/* Time Slots */}
            <div className="time-slots-scroll">
              <button className="scroll-btn left" onClick={(e) => {
                e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <div className="time-slots-container" ref={timeSlotsContainerRef}>
                {timeSlots && timeSlots.length > 0 ? (
                  timeSlots.map(time => {
                    const shiftLabel = getShiftLabelForTime(time);
                    const slotInfo = getSlotInfo(time);
                    const isBlocked = slotInfo.isBlocked;
                    // Waitlist slot: over capacity with capacity enforcement ON (allowOverbooking=false)
                    const isWaitlistSlot = slotInfo.isOverCapacity && !slotInfo.allowOverbooking;
                    const requiresApproval = slotInfo.requiresApproval;
                    // Waitlist slots are NOT unavailable - only manually blocked slots are
                    const isUnavailable = isBlocked;

                    // Determine which capacity to display:
                    // - If slot has a capacity range (maxCapacity differs from shiftMaxCapacity), use remainingCapacity (range-based)
                    // - If no capacity range, use shiftRemainingCovers (shift-wide)
                    const hasCapacityRange = slotInfo.maxCapacity !== null && slotInfo.shiftMaxCapacity !== null && slotInfo.maxCapacity !== slotInfo.shiftMaxCapacity;
                    const displayCapacity = hasCapacityRange ? slotInfo.remainingCapacity : slotInfo.shiftRemainingCovers;
                    const showLowCapacity = !isUnavailable && !isWaitlistSlot && displayCapacity !== null && displayCapacity > 0 && displayCapacity <= 30;

                    // Determine tooltip message
                    let tooltipMsg = shiftLabel || '';
                    if (isBlocked) {
                      tooltipMsg = 'This slot is not available';
                    } else if (requiresApproval) {
                      tooltipMsg = 'This slot requires approval';
                    } else if (isWaitlistSlot) {
                      tooltipMsg = 'You will be added to the waitlist';
                    } else if (displayCapacity !== null) {
                      tooltipMsg = `${displayCapacity} spots available`;
                    }

                    return (
                      <button
                        key={time}
                        type="button"
                        data-time={time}
                        className={`time-pill ${reservationDetails.time === time ? 'active' : ''} ${shiftLabel ? 'has-shift' : ''} ${isBlocked ? 'blocked' : ''} ${isWaitlistSlot ? 'waitlist' : ''} ${requiresApproval ? 'requires-approval' : ''}`}
                        onClick={() => !isUnavailable && handleTimeSelection(time)}
                        disabled={isUnavailable}
                        title={tooltipMsg}
                      >
                        {formatTime(time)}
                        {isBlocked && (
                          <i className="bi bi-lock-fill blocked-icon"></i>
                        )}
                        {requiresApproval && !isBlocked && (
                          <span className="approval-indicator">
                            <i className="bi bi-shield-check"></i>
                          </span>
                        )}
                        {isWaitlistSlot && (
                          <span className="waitlist-indicator">
                            <i className="bi bi-hourglass-split"></i> Waitlist
                          </span>
                        )}
                        {shiftLabel && visibleShifts.length > 0 && !isUnavailable && (
                          <span></span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <p className="text-muted">
                    {mustChooseShift
                      ? 'Choose an option above to see the available times'
                      : 'No time slots available'}
                  </p>
                )}
              </div>
              <button className="scroll-btn right" onClick={(e) => {
                e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>



            {/* Show approval hint when slot requires approval */}
            {reservationDetails.time && getSlotInfo(reservationDetails.time).requiresApproval && (
              <div className="approval-hint mt-2">
                <i className="bi bi-shield-check me-2"></i>
                <span>This time slot requires approval. You will receive a confirmation once your request is reviewed.</span>
              </div>
            )}
          </div>
          )}

          {/* Floor plan - seat booking restaurants: pick whole tables (and single bar
              stools) for the chosen show night */}
          {seatMode && reservationDetails.date && !isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
          <div className="form-section seat-map-section">
            <label className={`form-label ${fieldErrors.seats ? 'text-danger fw-bold' : ''}`}>
              Choose your tables {fieldErrors.seats && <span className="text-danger">(required)</span>}
            </label>
            {!seatShiftId ? (
              <p className="form-hint text-muted">Choose a time above to see the floor plan</p>
            ) : (
              <>
                <p className="form-hint text-muted">
                  Tap a table to book it, with all its seats; pick as many tables as you like. Bar seats are booked one by one.
                  Your tables are yours for the whole {seatShift?.label || 'show'}.
                </p>
                {seatMapLoading && !seatMap && (
                  <div className="seat-map-loading">
                    <span className="spinner-border spinner-border-sm me-2" />Loading the floor plan…
                  </div>
                )}
                {seatMapError && <div className="seat-map-error">{seatMapError}</div>}
                {seatMap && (
                  <SeatMap
                    tables={seatMap.tables}
                    components={seatMap.components}
                    categories={seatMap.categories}
                    selectedSeatIds={selectedSeatIds}
                    fallbackTicketCents={seatFallbackTicketCents}
                    onToggleTable={handleToggleTable}
                    onToggleSeat={handleToggleSeat}
                  />
                )}
                {seatNotice && (
                  <div className="seat-map-notice mt-2">
                    <i className="bi bi-exclamation-circle me-2"></i>{seatNotice}
                  </div>
                )}
                {seatMap && selectedSeatIds.length > 0 && (
                  <div className="mt-3">
                    <SelectionSummary
                      selection={describeSelection(seatMap, selectedSeatIds, seatFallbackTicketCents)}
                      onRemove={(item) => {
                        setSeatNotice('');
                        setSelectedSeatIds(selectedSeatIds.filter((id) => !item.seatIds.includes(id)));
                      }}
                      onClear={() => {
                        setSeatNotice('');
                        setSelectedSeatIds([]);
                      }}
                    />
                    {/* Unpaid tables are not held, so say so whenever the show takes payment. */}
                    {!!seatShift?.paymentMode && seatShift.paymentMode !== 'none' && (
                      <p className="form-hint text-muted mt-2 mb-0">
                        Your tables are secured once payment is complete. Until then, other guests can still book them.
                      </p>
                    )}
                  </div>
                )}
                {seatMap && selectedSeatIds.length > 0 && (
                  <div className="seat-gender-counts">
                    <label className={`form-label ${fieldErrors.genderCounts ? 'text-danger fw-bold' : ''}`}>
                      Who's coming? {fieldErrors.genderCounts && <span className="text-danger">(required)</span>}
                    </label>
                    {[
                      { field: 'men', label: 'Men', value: menCount },
                      { field: 'women', label: 'Women', value: womenCount },
                    ].map(({ field, label, value }) => (
                      <div key={field} className="seat-gender-row">
                        <span className="seat-gender-label">{label}</span>
                        <div className="ticket-quantity-controls">
                          <button
                            type="button"
                            className="qty-btn minus"
                            onClick={() => changeGenderCount(field, -1)}
                            disabled={value === 0}
                            aria-label={`Fewer ${field}`}
                          >
                            <i className="bi bi-dash"></i>
                          </button>
                          <span className="qty-value">{value}</span>
                          <button
                            type="button"
                            className="qty-btn plus"
                            onClick={() => changeGenderCount(field, 1)}
                            disabled={menCount + womenCount >= selectedSeatIds.length}
                            aria-label={`More ${field}`}
                          >
                            <i className="bi bi-plus"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                    <p className="form-hint text-muted">
                      {menCount + womenCount} {menCount + womenCount === 1 ? 'person' : 'people'} coming for {selectedSeatIds.length} seat{selectedSeatIds.length === 1 ? '' : 's'}.
                      {menCount + womenCount > 0 && menCount + womenCount < selectedSeatIds.length && ' A smaller group is fine: the tables are paid in full.'}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Entrance rule for every seat booking guest, in English and Arabic. Turns red
                when the counts are complete and break the rule; the guest can't continue then. */}
            {(() => {
              const minWomenPercent = 100 - maxMenPercent;
              const countsComplete = selectedSeatIds.length > 0 && menCount + womenCount > 0;
              const ratio = getGenderRatio(menCount, womenCount, maxMenPercent);
              const notMet = countsComplete && !ratio.met;
              return (
                <div className={`seat-rule-note ${notMet ? 'seat-rule-note-not-met' : ''}`}>
                  <i className={`bi ${notMet ? 'bi-exclamation-triangle-fill' : 'bi-info-circle'}`}></i>
                  <div className="seat-rule-note-body">
                    <p>
                      Entrance rule: each group must be no more than {maxMenPercent}% men (at least {minWomenPercent}% women).
                      Groups that don't meet this rule will not be allowed into the show. All-women groups are welcome.
                    </p>
                    {notMet && (
                      <p className="seat-rule-note-warning">
                        Your group is {menCount} men and {womenCount} women ({ratio.menPercent}% men), which does not meet this rule.
                        Change the counts to continue.
                      </p>
                    )}
                    <div className="seat-rule-note-ar" dir="rtl" lang="ar">
                      <p>
                        قاعدة الدخول: يجب ألا تزيد نسبة الرجال في المجموعة عن {maxMenPercent}% (وألا تقل نسبة السيدات عن {minWomenPercent}%). المجموعات التي لا تلتزم بهذه القاعدة لن يُسمح لها بدخول العرض. المجموعات المكوّنة من سيدات فقط مرحّب بها.
                      </p>
                      {notMet && (
                        <p className="seat-rule-note-warning">
                          مجموعتك تضم {menCount} رجال و{womenCount} سيدات (نسبة الرجال {ratio.menPercent}%)، وهذا لا يطابق قاعدة الدخول. عدّل العدد لإكمال الحجز.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          )}

          {/* Table for - Only show when no event choice needed OR user chose regular reservation, and date is not blocked/sold-out */}
          {(!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
          <div className="form-section">
            {/* Seat booking: the party size comes from the seats picked on the map */}
            {!seatMode && (
            <>
            <label className={`form-label ${fieldErrors.guests ? 'text-danger fw-bold' : ''}`}>Table for {fieldErrors.guests && <span className="text-danger">(required)</span>}</label>
            <div className="guests-scroll">
              <button className="scroll-btn left" onClick={(e) => {
                e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <div className="guests-container">
                {Array.from({ length: maxGuestsForPills }, (_, i) => i + 1).map(num => (
                  <button
                    key={num}
                    type="button"
                    className={`guest-pill ${reservationDetails.guests === num && !showCustomGuestInput ? 'active' : ''}`}
                    onClick={() => {
                      handleStep1Change('guests', num);
                      setShowCustomGuestInput(false);
                      setCustomGuestValue('');
                    }}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  className={`guest-pill more-guests ${showCustomGuestInput ? 'active' : ''}`}
                  onClick={() => {
                    setShowCustomGuestInput(!showCustomGuestInput);
                    if (!showCustomGuestInput) {
                      setCustomGuestValue(reservationDetails.guests > maxGuestsForPills ? reservationDetails.guests.toString() : '');
                    }
                  }}
                  title="Enter a larger number"
                >
                  {showCustomGuestInput ? <i className="bi bi-x-lg"></i> : <i className="bi bi-plus-lg"></i>}
                </button>
              </div>
              <button className="scroll-btn right" onClick={(e) => {
                e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>

            {/* Custom guest input below pills */}
            {showCustomGuestInput && (
              <div className="custom-guest-below">
                <div className="custom-guest-input-row">
                  <i className="bi bi-people-fill"></i>
                  <input
                    type="number"
                    className="custom-guest-input-field"
                    value={customGuestValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomGuestValue(val);
                      const num = parseInt(val);
                      if (num >= 1 && num <= 250) {
                        handleStep1Change('guests', num);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    min="1"
                    max="250"
                    placeholder={`${maxGuestsForPills + 1}+ guests`}
                    autoFocus
                  />
                </div>
                {customGuestValue && (parseInt(customGuestValue) < 1 || parseInt(customGuestValue) > 250) && (
                  <small className="text-danger mt-2 d-block">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    Please enter a number between 1 and 250
                  </small>
                )}
              </div>
            )}
            </>
            )}

            {/* Waitlist hint for large party (not for seat booking: the seats are the capacity) */}
            {reservationDetails.time && !seatMode && (() => {
              const slotInfo = getSlotInfo(reservationDetails.time);
              const maxAllowedGuests = slotInfo.maxAllowedGuestsNumber;
              const willBeWaitlisted = maxAllowedGuests && reservationDetails.guests > maxAllowedGuests;
              const shiftAtCapacity = slotInfo.shiftRemainingCovers !== null && slotInfo.shiftRemainingCovers < reservationDetails.guests;

              if (willBeWaitlisted) {
                return (
                  <div className="waitlist-hint mt-2">
                    <i className="bi bi-info-circle me-2"></i>
                    <span>
                      Reservations for more than {maxAllowedGuests} guests require approval.
                      You will be placed on the waitlist.
                    </span>
                  </div>
                );
              }

              if (shiftAtCapacity && !slotInfo.allowOverbooking) {
                return (
                  <div className="waitlist-hint mt-2">
                    <i className="bi bi-info-circle me-2"></i>
                    <span>
                      This time slot has limited availability. Your booking may be placed on the waitlist.
                    </span>
                  </div>
                );
              }

              return null;
            })()}

            {/* Show payment info for automatic payment mode with a payment group
                (not for seat booking: the seat summary already shows the total) */}
            {!seatMode && reservationDetails.time && reservationDetails.guests &&
              getShiftForTime(reservationDetails.time)?.paymentMode === 'automatic' &&
              getShiftForTime(reservationDetails.time)?.defaultPaymentGroup && (() => {
              const shift = getShiftForTime(reservationDetails.time);
              // When the booking will go to the waitlist (needs approval first), hide
              // the deposit amount — the venue may change the payment group manually
              // before confirming, so any amount shown now could be wrong.
              const wlSlotInfo = getSlotInfo(reservationDetails.time);
              const willBeWaitlisted =
                (wlSlotInfo.maxAllowedGuestsNumber && reservationDetails.guests > wlSlotInfo.maxAllowedGuestsNumber) ||
                (wlSlotInfo.shiftRemainingCovers !== null && wlSlotInfo.shiftRemainingCovers < reservationDetails.guests && !wlSlotInfo.allowOverbooking);
              if (willBeWaitlisted) return null;
              // Resolve effective payment group based on guest-count rules (when configured),
              // otherwise fall back to shift.defaultPaymentGroup (existing behavior).
              const paymentGroup = resolvePaymentGroup(shift, reservationDetails.guests);
              const basePriceCents = paymentGroup.priceCents;
              const priceExcludesVat = paymentGroup.priceExcludesVat;
              const vatMultiplier = priceExcludesVat ? 1.14 : 1;
              const isPerGuest = paymentGroup.paymentType === 'per_guest';

              const baseTotal = isPerGuest
                ? (basePriceCents / 100) * reservationDetails.guests
                : (basePriceCents / 100);

              const vatAmount = priceExcludesVat ? baseTotal * 0.14 : 0;
              const finalTotal = baseTotal * vatMultiplier;

              return (
                <div className="deposit-info mt-2">
                  <p className="deposit-hint">
                    <i className="bi bi-info-circle me-1"></i>
                    {/* Per-guest: "A deposit of (n × price) is required … which totals EGP X".
                        Flat (per-reservation): "A deposit of EGP X is required …". */}
                    A deposit of{' '}
                    {isPerGuest ? (
                      <>
                        <span className="deposit-breakdown">({reservationDetails.guests} × EGP {(basePriceCents / 100).toFixed(2)})</span>
                        {' '}is required to confirm your reservation, total is{' '}
                        <strong>EGP {priceExcludesVat ? finalTotal.toFixed(2) : baseTotal.toFixed(2)}</strong>.
                      </>
                    ) : (
                      <>
                        <strong>EGP {priceExcludesVat ? finalTotal.toFixed(2) : baseTotal.toFixed(2)}</strong>
                        {' '}is required to confirm your reservation.
                      </>
                    )}
                    {priceExcludesVat && (
                      <span className="deposit-vat"> (incl. {vatAmount.toFixed(2)} VAT)</span>
                    )}
                  </p>

                  {/* Promo code for this reservation. Validated against the
                      restaurant + booking date, so a code locked to one day is
                      rejected on any other date. */}
                  <div className="res-promo">
                    {!resPromo && (
                      <div className="res-promo-row">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Promo code (optional)"
                          value={resPromoInput}
                          onChange={(e) => { setResPromoInput(e.target.value); setResPromoError(''); }}
                          disabled={resPromoLoading}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={resPromoLoading || !resPromoInput.trim()}
                          onClick={async () => {
                            const code = resPromoInput.trim();
                            if (!code) return;
                            setResPromoLoading(true);
                            setResPromoError('');
                            try {
                              const result = await validateReservationPromoCodePublic({
                                code,
                                restaurantId,
                                date: reservationDetails.date,
                                pricePerGuestCents: basePriceCents,
                                guests: isPerGuest ? reservationDetails.guests : 1,
                                buyerEmail: reservationDetails.email || undefined,
                              });
                              setResPromo({ ...result, code });
                            } catch (err) {
                              const raw = err.response?.data?.message;
                              setResPromoError(
                                Array.isArray(raw) ? raw.join(', ')
                                  : (typeof raw === 'string' ? raw : 'Could not validate promo code')
                              );
                            } finally {
                              setResPromoLoading(false);
                            }
                          }}
                        >
                          {resPromoLoading ? <span className="spinner-border spinner-border-sm" /> : 'Apply'}
                        </button>
                      </div>
                    )}

                    {resPromo && (() => {
                      const discountEgp = (resPromo.totalDiscountCents || 0) / 100;
                      const newTotal = Math.max(0, (priceExcludesVat ? finalTotal : baseTotal) - discountEgp * vatMultiplier);
                      return (
                        <div className="res-promo-applied">
                          <span>
                            <strong>{resPromo.code}</strong> · {resPromo.discountPercent}% off —
                            new total <strong>EGP {newTotal.toFixed(2)}</strong>
                          </span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm"
                            onClick={() => { setResPromo(null); setResPromoInput(''); }}
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })()}

                    {resPromoError && <div className="res-promo-error">{resPromoError}</div>}
                  </div>
                </div>
              );
            })()}

            {/* Per-shift widget note (admin-authored) — e.g. clarifying what the deposit covers.
                Shown for the shift the guest is booking under. Use the actively selected
                (labeled) shift too, not just the time-derived one: selecting a shift pill
                clears the chosen time, so keying off selectedShift alone would hide the note
                until a time slot is re-picked. */}
            {(() => {
              const noteShift = selectedShift || activeLabeledShift;
              if (!noteShift?.widgetNote) return null;
              return (
                <div className="shift-widget-note mt-2">
                  <i className="bi bi-info-circle me-2"></i>
                  <span>{noteShift.widgetNote}</span>
                </div>
              );
            })()}
          </div>
          )}

          {/* Preferred Zone - Only show if zones exist AND for regular reservations AND date is not blocked/sold-out */}
          {(!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && zones.length > 0 && !seatMode && (
            <div className="form-section">
              <label className="form-label">Preferred Zone (Optional)</label>
              <p className="form-hint text-muted">Select your preferred seating area</p>
              <div className="zone-pills-scroll">
                <button className="scroll-btn left" onClick={(e) => {
                  e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="zone-pills-container">
                  <button
                    type="button"
                    className={`zone-pill ${selectedZoneId === null ? 'active' : ''}`}
                    onClick={() => setSelectedZoneId(null)}
                  >
                    No Preference
                  </button>
                  {zones.map(zone => (
                    <button
                      key={zone.id}
                      type="button"
                      className={`zone-pill ${selectedZoneId === zone.id ? 'active' : ''}`}
                      onClick={() => setSelectedZoneId(zone.id)}
                    >
                      {zone.name}
                    </button>
                  ))}
                </div>
                <button className="scroll-btn right" onClick={(e) => {
                  e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}

          {/* Special Occasion - Only show for regular reservations AND date is not blocked/sold-out
              (hidden for a restaurant whose occasions are all switched off) */}
          {showSpecialOccasion && (!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
          <div className="form-section">
            {occasionsMode ? (
              <SpecialOccasionHeader
                id="hasSpecialOccasion"
                checked={hasSpecialOccasion}
                onChange={handleSpecialOccasionToggle}
              />
            ) : (
              <div className="special-occasion-check">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  id="hasSpecialOccasion"
                  checked={hasSpecialOccasion}
                  onChange={(e) => handleSpecialOccasionToggle(e.target.checked)}
                />
                <label className="checkbox-label" htmlFor="hasSpecialOccasion">
                  <i className="bi bi-star-fill me-2"></i>
                  Special Occasion
                </label>
              </div>
            )}
            {hasSpecialOccasion && (
              <div className="mt-3">
                {occasionsMode ? (
                  <OccasionPicker
                    occasions={celebrationOccasions}
                    value={celebrationSelection}
                    onChange={(selection) => {
                      setCelebrationSelection(selection);
                      if (fieldErrors.celebrationOccasion || fieldErrors.celebrationPackage) {
                        setFieldErrors((prev) => ({ ...prev, celebrationOccasion: false, celebrationPackage: false }));
                      }
                    }}
                    note={reservationDetails.specialOccasion}
                    onNoteChange={(value) => handleStep1Change('specialOccasion', value)}
                    errors={{
                      occasion: !!fieldErrors.celebrationOccasion,
                      package: !!fieldErrors.celebrationPackage,
                    }}
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      className="form-control modern-input"
                      id="specialOccasion"
                      value={reservationDetails.specialOccasion}
                      onChange={(e) => handleStep1Change('specialOccasion', e.target.value)}
                      placeholder="e.g., Birthday, Anniversary, Engagement..."
                      maxLength={200}
                    />
                    <small className="form-text text-muted">
                      Let us know what you're celebrating!
                    </small>
                  </>
                )}
              </div>
            )}
          </div>
          )}

          {/* Upcoming Events Section - merged internal + external */}
          {(() => {
            // On the dedicated single-experience page the guest is booking ONE
            // specific event — don't show the "Highlighted Experiences" list of
            // other events; it's irrelevant and distracting there.
            if (singleEvent) return null;
            const normalizedInternal = events
              .filter(e => e.isActive !== false)
              // Only experiences flagged "Highlighted" appear in this section.
              // Treat missing flag as highlighted (backward-compatible with rows
              // created before the field existed / API not yet rebuilt).
              .filter(e => e.isHighlighted !== false)
              .map(e => ({
                ...e,
                title: e.name,
                dateFormatted: new Date(e.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                time: e.startTime ? e.startTime : '',
                shortDescription: e.description || '',
                isExternal: false,
                _sourceId: `internal-${e.id}`,
              }));
            // Deduplicate: skip external events that match an internal event by name
            const internalNames = new Set(normalizedInternal.map(e => e.title?.toLowerCase().trim()));
            const normalizedExternal = externalEvents
              .filter(e => e.isActive !== false)
              // Same highlight gate as the internal list (the external feed now
              // carries isHighlighted). Without this, an un-highlighted event's
              // external twin would reappear once it drops out of the internal list.
              .filter(e => e.isHighlighted !== false)
              .filter(e => !internalNames.has(e.title?.toLowerCase().trim()))
              .map(e => ({
                ...e,
                isExternal: true,
                _sourceId: `external-${e.id}`,
              }));
            // When the guest is already viewing an experience's own day, that
            // experience is presented as the banner above — don't also list it
            // here (it reads as a confusing duplicate "other" event).
            const currentDayEventNames = new Set(
              bookingTypeEvents
                .map(e => (e.title || e.name)?.toLowerCase().trim())
                .filter(Boolean)
            );
            // Show experiences in chronological order (soonest first), regardless
            // of which feed (internal/external) they came from.
            const allEvents = [...normalizedInternal, ...normalizedExternal]
              .filter(ev => !currentDayEventNames.has(ev.title?.toLowerCase().trim()))
              .sort((a, b) => {
                const da = new Date(a.eventDate || a.date).getTime();
                const db = new Date(b.eventDate || b.date).getTime();
                if (Number.isNaN(da)) return 1;
                if (Number.isNaN(db)) return -1;
                return da - db;
              });
            if (allEvents.length === 0) return null;
            return (
              <div className="form-section events-section-form">
                <h4 className="events-section-title">
                  <i className="bi bi-calendar-event me-2"></i>
                  Highlighted Experiences
                </h4>
                <div className="events-grid-form">
                  {allEvents.map((event) => (
                    <EventCard
                      key={event._sourceId || event.id}
                      event={event}
                      compact
                      onClick={() => {
                        if (event.isExternal && event.eventUrl) {
                          window.open(event.eventUrl, '_blank', 'noopener,noreferrer');
                        } else if (isPageOnlyEvent(event)) {
                          // "Book from Widget" OFF → this experience can't be booked
                          // inside the widget; open its dedicated page instead (same
                          // as the banner: Kiki's site for Kiki's, else /experience/:id).
                          openEventDedicatedPage(event);
                        } else {
                          // Internal event: set the date and trigger ticket selection
                          const eventDate = event.eventDate
                            ? toLocalDateString(new Date(event.eventDate))
                            : '';
                          if (eventDate) {
                            if (isDateSoldOut(eventDate)) {
                              handleStep1Change('date', eventDate);
                            } else {
                              setReservationDetails(prev => ({ ...prev, date: eventDate }));
                              setBookingTypeChoice('tickets');
                              setBookingTypeEvents([event]);
                              setSelectedEvent(event);
                              setSelectedTickets([]);
                              setTicketTotal(0);

                              // Scroll the matching date pill into view
                              setDateViewMode('quick');
                              setTimeout(() => {
                                if (quickDatesContainerRef.current) {
                                  const pills = quickDatesContainerRef.current.querySelectorAll('.quick-date-pill');
                                  for (const pill of pills) {
                                    if (pill.classList.contains('active')) {
                                      pill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                                      break;
                                    }
                                  }
                                }
                              }, 150);

                              // Scroll to top of form
                              if (formContainerRef.current) {
                                formContainerRef.current.scrollIntoView({ behavior: 'smooth' });
                              }
                            }
                          }
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Widget Form Note - Only show in widget mode */}
          {isWidget && restaurant?.widgetFormNote && restaurant.widgetFormNote.replace(/<[^>]*>/g, '').trim() && externalEventsLoaded && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(restaurant.widgetFormNote) }}
              />
            </div>
          )}

          {/* Event Form House Rules - Only show on event dates */}
          {isWidget && bookingTypeChoice === 'tickets' && activeNoteEvent?.formHouseRules && activeNoteEvent.formHouseRules.replace(/<[^>]*>/g, '').trim() && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(activeNoteEvent.formHouseRules) }}
              />
            </div>
          )}

          {/* Next Button - Only show for regular reservations AND date is not blocked/sold-out */}
          {(!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
            <button type="button" className="btn btn-next" onClick={handleNext}>
              Next
            </button>
          )}
        </div>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} className="step-content step-2-content" noValidate>
          <h4 className="step-title">Step 2: Your Information</h4>

          <div className="form-section">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="firstName" className="form-label">First Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control modern-input ${fieldErrors.firstName ? 'is-invalid' : ''}`}
                  id="firstName"
                  value={personalInfo.firstName}
                  onChange={(e) => handleStep2Change('firstName', e.target.value)}
                  placeholder="Your First Name"
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label">Last Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control modern-input ${fieldErrors.lastName ? 'is-invalid' : ''}`}
                  id="lastName"
                  value={personalInfo.lastName}
                  onChange={(e) => handleStep2Change('lastName', e.target.value)}
                  placeholder="Your Last Name"
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email <span className="text-danger">*</span></label>
              <input
                type="email"
                className={`form-control modern-input ${fieldErrors.email ? 'is-invalid' : ''}`}
                id="email"
                value={personalInfo.email}
                onChange={(e) => handleStep2Change('email', e.target.value)}
                placeholder="Your Email Address"
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="phone" className="form-label">Phone Number <span className="text-danger">*</span></label>
              <PhoneInput
                country={'eg'}
                value={personalInfo.phone}
                onChange={(phone) => handleStep2Change('phone', '+' + phone)}
                inputProps={{
                  id: 'phone',
                  required: true,
                }}
                containerClass="phone-input-container"
                inputClass={`form-control modern-input ${fieldErrors.phone ? 'is-invalid' : ''}`}
                buttonClass="phone-input-button"
                enableSearch={true}
                searchPlaceholder="Search country..."
                preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
              />
            </div>

            {selectedShift?.requireSocialMedia && (
              <div className="mb-3">
                <label htmlFor="socialMediaLink" className="form-label">
                  Social Media Link
                  {selectedShift.socialMediaRequired && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="url"
                  className="form-control modern-input"
                  id="socialMediaLink"
                  value={personalInfo.socialMediaLink}
                  onChange={(e) => handleStep2Change('socialMediaLink', e.target.value)}
                  placeholder="https://instagram.com/yourhandle"
                  required={selectedShift.socialMediaRequired}
                />
              </div>
            )}

            {collectAllGuests && (
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Guest Details<span className="text-danger"> *</span>
                </label>
                <p className="text-muted small mb-3">
                  {seatMode
                    ? "Each guest gets their own QR code by email. You can use your own email for guests who don't have one."
                    : 'Please provide the details for each guest in your party.'}
                </p>
                {guestDetails.map((guest, index) => (
                  <div key={index} className="ticket-holder-card">
                    <div className="ticket-holder-header">
                      <h5>
                        <i className="bi bi-person me-1"></i>
                        Guest {index + 1}
                        {index === 0 && (
                          <span className="text-muted fw-normal small ms-2">(lead booker)</span>
                        )}
                      </h5>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">First Name *</label>
                        <input
                          type="text"
                          className="form-control modern-input"
                          value={guest.firstName}
                          onChange={(e) => handleGuestDetailChange(index, 'firstName', e.target.value)}
                          placeholder="First Name"
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Last Name *</label>
                        <input
                          type="text"
                          className="form-control modern-input"
                          value={guest.lastName}
                          onChange={(e) => handleGuestDetailChange(index, 'lastName', e.target.value)}
                          placeholder="Last Name"
                          required
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          className="form-control modern-input"
                          value={guest.email}
                          onChange={(e) => handleGuestDetailChange(index, 'email', e.target.value)}
                          placeholder="Email Address"
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{seatMode ? 'Phone (Optional)' : 'Phone *'}</label>
                        <PhoneInput
                          country={'eg'}
                          value={guest.phone}
                          // Optional for seat booking: only the country code counts as no phone.
                          onChange={(phone, country) => handleGuestDetailChange(index, 'phone', seatMode && phone === country?.dialCode ? '' : '+' + phone)}
                          inputProps={{ required: !seatMode }}
                          containerClass="phone-input-container"
                          inputClass="form-control modern-input"
                          buttonClass="phone-input-button"
                          enableSearch={true}
                          searchPlaceholder="Search country..."
                          preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
                        />
                      </div>
                    </div>
                    {selectedShift?.requireSocialMedia && (
                      <div className="row">
                        <div className="col-12 mb-3">
                          <label className="form-label">
                            Social Media Link
                            {selectedShift.socialMediaRequired && <span className="text-danger"> *</span>}
                          </label>
                          <input
                            type="url"
                            className="form-control modern-input"
                            value={guest.socialMediaLink || ''}
                            onChange={(e) => handleGuestDetailChange(index, 'socialMediaLink', e.target.value)}
                            placeholder="https://instagram.com/yourhandle"
                            required={selectedShift.socialMediaRequired}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mb-3">
              <div
                className="collapsible-header"
                onClick={() => setAllergiesExpanded(!allergiesExpanded)}
              >
                <label className="form-label mb-0">
                  <i className={`bi bi-chevron-${allergiesExpanded ? 'down' : 'right'} me-2`}></i>
                  Please let us know if you have any allergies
                  {personalInfo.allergies.length > 0 && (
                    <span className="badge ms-2">
                      {personalInfo.allergies.length} selected
                    </span>
                  )}
                </label>
              </div>
              {allergiesExpanded && (
                <div className="multi-select-group mt-2">
                  {['Peanuts', 'Shellfish', 'Dairy', 'Gluten', 'Eggs', 'Soy', 'Tree Nuts', 'Fish', 'Other'].map((allergy) => (
                    <div key={allergy} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`allergy-${allergy}`}
                        checked={personalInfo.allergies.includes(allergy)}
                        onChange={(e) => {
                          const newAllergies = e.target.checked
                            ? [...personalInfo.allergies, allergy]
                            : personalInfo.allergies.filter(a => a !== allergy);
                          handleStep2Change('allergies', newAllergies);
                        }}
                      />
                      <label className="form-check-label" htmlFor={`allergy-${allergy}`}>
                        {allergy}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-3">
              <div
                className="collapsible-header"
                onClick={() => setDietaryExpanded(!dietaryExpanded)}
              >
                <label className="form-label mb-0">
                  <i className={`bi bi-chevron-${dietaryExpanded ? 'down' : 'right'} me-2`}></i>
                  Any dietary preference
                  {personalInfo.dietaryPreference.length > 0 && (
                    <span className="badge bg-info ms-2">
                      {personalInfo.dietaryPreference.length} selected
                    </span>
                  )}
                </label>
              </div>
              {dietaryExpanded && (
                <div className="multi-select-group mt-2">
                  {['Vegetarian', 'Vegan', 'Halal',  'Pescatarian', 'Gluten-Free', 'Dairy-Free'].map((preference) => (
                    <div key={preference} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`dietary-${preference}`}
                        checked={personalInfo.dietaryPreference.includes(preference)}
                        onChange={(e) => {
                          const newPreferences = e.target.checked
                            ? [...personalInfo.dietaryPreference, preference]
                            : personalInfo.dietaryPreference.filter(p => p !== preference);
                          handleStep2Change('dietaryPreference', newPreferences);
                        }}
                      />
                      <label className="form-check-label" htmlFor={`dietary-${preference}`}>
                        {preference}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="comments" className="form-label">Additional Requests/Comments:</label>
              <textarea
                className="form-control modern-textarea"
                id="comments"
                rows="4"
                value={personalInfo.comments}
                onChange={(e) => handleStep2Change('comments', e.target.value)}
                placeholder="Any special requests or comments..."
              ></textarea>
            </div>

            {/* Payment method (National vs International) intentionally removed from
                regular reservations — International (USD) cards apply to Experiences
                only. Regular reservations always pay in EGP (selectedCurrency stays
                'EGP'). */}

            {selectedCelebrationPackage && (
              <div className="deposit-info mb-3">
                <p className="deposit-hint mb-0">
                  <i className="bi bi-gift me-1"></i>
                  Your <strong>{selectedCelebrationPackage.name}</strong> {selectedOccasion.name.toLowerCase()} package
                  {selectedCelebrationPackage.addonName && celebrationSelection.addonQuantity > 0 && (
                    <> with {celebrationSelection.addonQuantity} × {selectedCelebrationPackage.addonName}</>
                  )}
                  {' '}(<strong>EGP {(celebrationTotalCents(selectedCelebrationPackage, celebrationSelection.addonQuantity) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong>, taxes and service included)
                  {' '}is added to your payment on the next page.
                </p>
              </div>
            )}
          </div>

          {/* Widget Form Note - Also show in step 2 */}
          {isWidget && restaurant?.widgetFormNote && restaurant.widgetFormNote.replace(/<[^>]*>/g, '').trim() && externalEventsLoaded && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(restaurant.widgetFormNote) }}
              />
            </div>
          )}

          {/* Event Form House Rules - Also show in step 2 */}
          {isWidget && bookingTypeChoice === 'tickets' && activeNoteEvent?.formHouseRules && activeNoteEvent.formHouseRules.replace(/<[^>]*>/g, '').trim() && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(activeNoteEvent.formHouseRules) }}
              />
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-back"
              onClick={() => {
                setStep(1);
                scrollToTop();
              }}
            >
              Back
            </button>
            {!isLoggedIn && !isWidget && (
              <button
                type="button"
                className="btn btn-submit"
                onClick={handleLoginRedirect}
              >
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Login
              </button>
            )}
            <button
              type="submit"
              className="btn btn-submit btn-primary-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : (
                (isWidget || isLoggedIn) ? 'Complete Reservation' : 'Continue as a guest'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ReservationForm;
